// frontend/src/components/dashboard/DetalleEquipo.jsx
// DETALLE COMPLETO DEL EQUIPO BASADO EN EL TEMPLATE HTML ORIGINAL
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import ListaUnificada from '../common/ListaUnificada.jsx';
import SearchableSelectField from '../common/SearchableSelectField.jsx';
import SearchSuggestField from '../common/SearchSuggestField.jsx';
import Table from '../common/Table.jsx';
import Pagination from '../common/Pagination.jsx';
import { ActionButton } from '../common/Buttons';
import apiClient from '../../services/apiClient';
import { LinkEquipo } from '../common/Links.jsx';
import ModalDocumentacion from '../formularios/ModalDocumentacion';
import ModalAdjuntosDocumentacion from '../formularios/ModalAdjuntosDocumentacion';
import ModalParteDiario from '../formularios/ModalParteDiario.jsx';
import ModalRegistroLubricante from '../formularios/ModalRegistroLubricante.jsx';
import ConfiguracionBase from '../configuracion/ConfiguracionBase.jsx';
import { planService } from '../../services/planService';
import otcService from '../../services/otcService.js';
import mantenimientoService from '../../services/mantenimientoService';
import equiposService from '../../services/equiposService';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import { checkListService, imprimirCheckList } from '../../services/checkListService';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ModalConfirmacion';
import { useAuth } from '../../hooks/useAuth.js';
import ModalMantenimientoInicial from '../mantenimiento/ModalMantenimientoInicial.jsx';
import ModalPlanEquipo from '../mantenimiento/ModalPlanEquipo.jsx';
import { useEntrada } from '../common/ModalEntrada';
import ModalMapaGPSEquipo from '../equipos/ModalMapaGPSEquipo';
import { hoyArgentina, toLocalYMD } from '../../utils/dateUtils';

// CONTENEDOR PRINCIPAL DEL DETALLE
const DetalleContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.bgPrincipal};
  overflow: hidden;
`;

// HEADER CON PESTAÑAS Y BOTONES
const TabHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-bottom: 2px solid ${props => props.theme.colors.borderDefault};
  background: ${props => props.theme.colors.surface};
  gap: 8px;
  min-width: 0;
`;

// LISTA DE PESTAÑAS
const TabsList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1 1 0;
  gap: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  min-width: 0;
  &::-webkit-scrollbar { display: none; }
`;

const TabItem = styled.li`
  margin: 0;
  flex-shrink: 0;
`;

const LinkAdjuntos = styled.a`
  color: ${({ theme }) => theme.colors.info};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TabLink = styled.a`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 10px;
  text-decoration: none;
  white-space: nowrap;
  color: ${props => props.$active ? props.theme.colors.onPrimary : props.theme.colors.txtSecondary};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  border-bottom: ${props => props.$active ? `2px solid ${props.theme.colors.primary}` : '1px solid transparent'};
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-size: 0.82rem;
  transition-property: background-color, color, border-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;

  &:hover {
    color: ${props => props.$active ? props.theme.colors.onPrimary : props.theme.colors.primary};
    background: ${props => props.$active ? props.theme.colors.primaryHover : props.theme.colors.surfaceHover};
  }

  i {
    font-size: 0.85rem;
    color: inherit;
    opacity: ${props => props.$active ? 1 : 0.7};
  }
`;

// BOTONES DEL HEADER
const HeaderButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  white-space: nowrap;
  flex-shrink: 0;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.borderDefault};
  color: ${props => props.theme.colors.txtSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.82rem;
  transition-property: background-color, color, border-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;

  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
    color: ${props => props.theme.colors.primary};
  }

  &.btn-primary {
    background: ${props => props.theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
    border-color: ${props => props.theme.colors.primary};

    &:hover {
      background: ${props => props.theme.colors.primaryHover};
      border-color: ${props => props.theme.colors.primaryHover};
    }
  }

  i {
    font-size: 0.82rem;
    color: inherit;
  }
`;

const HoverRowActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  @media (hover: hover) and (pointer: fine) {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease;

    tr:hover &, tr:focus-within & {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const ProgrammedActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
  margin-left: auto;
  flex-shrink: 0;
`;

const ProgrammedSummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 4px;

  @media (max-width: 1280px) {
    gap: 10px;
  }

  @media (max-width: 992px) {
    gap: 8px;
  }

  &::-webkit-scrollbar {
    height: 5px;
  }
`;

const ProgrammedMetric = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1.2;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgSecundario};

  @media (max-width: 1280px) {
    font-size: 13px;
    gap: 5px;
    padding: 3px 7px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    gap: 4px;
    padding: 3px 6px;
  }
`;

const ProgrammedMetricLabel = styled.small`
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: 12px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const UrgenciaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid ${({ theme, $tone }) => {
    switch ($tone) {
      case 'excedido':
      case 'sin_plan': return theme.colors.danger;
      case 'urgente': return theme.colors.warning;
      case 'proximo': return theme.colors.info;
      case 'sin_calculo':
      case 'sin_gestion': return theme.colors.borderDefault;
      default: return theme.colors.success;
    }
  }};
  color: ${({ theme, $tone }) => {
    switch ($tone) {
      case 'excedido':
      case 'sin_plan': return theme.colors.danger;
      case 'urgente': return theme.colors.warning;
      case 'proximo': return theme.colors.info;
      case 'sin_calculo':
      case 'sin_gestion': return theme.colors.txtSecondary;
      default: return theme.colors.success;
    }
  }};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 0;
  padding: 0.55rem 0.75rem;
  background: ${({ theme }) => theme.colors.bgSecundario};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0;
`;

const SectionTitle = styled.h6`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.83rem;
  font-weight: 700;
`;

// CONTENIDO DE LAS PESTAÑAS
// El contenedor ya no hace scroll general; la lista interior se encargará del scroll.
const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* habilita scroll vertical interno */
  overflow-x: hidden;
  padding: 8px 8px 12px; /* respiración visual */
  scrollbar-width: thin;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: ${props => props.theme.colors.bgSecundario}; }
  &::-webkit-scrollbar-thumb { background: ${props => props.theme.colors.borderDefault}; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: ${props => props.theme.colors.primary}; }
`;

const TabPane = styled.div`
  display: ${props => props.$active ? 'flex' : 'none'};
  flex-direction: column;
  height: 100%;
  overflow: hidden; /* Controlar el overflow del contenedor */
`;

// TÍTULO DE LA PESTAÑA
const TabTitle = styled.h5`
  margin: 0 0 16px 0;
  color: ${props => props.theme.colors.txtPrincipal};
  font-size: 1.125rem;
  font-weight: 600;
`;

// GRID DE CARDS
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

// CARD INDIVIDUAL
const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.borderDefault};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  /* Removida altura fija para permitir contenido dinámico */
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  padding: 12px 16px;
  background: ${props => props.theme.colors.bgSecundario};
  border-bottom: 1px solid ${props => props.theme.colors.borderDefault};
  border-radius: ${props => props.theme.borderRadius.lg} ${props => props.theme.borderRadius.lg} 0 0;
  font-weight: 600;
  color: ${props => props.theme.colors.txtPrincipal};
  font-size: 0.9rem;
  flex-shrink: 0; /* No se encoge */
`;

const CardBody = styled.div`
  padding: 10px 16px;
  /* Removido flex:1 y overflow para permitir altura dinámica */
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 8px;
  row-gap: 5px;
  align-items: center;
`;

const CardRow = styled.p`
  display: contents;
  font-size: 0.875rem;
`;

const CardLabel = styled.strong`
  color: ${props => props.theme.colors.txtPrincipal};
  font-weight: 600;
  white-space: nowrap;
`;

const CardValue = styled.span`
  color: ${props => props.theme.colors.txtSecondary};
  min-width: 0;
`;

// MENSAJE CUANDO NO HAY EQUIPO SELECCIONADO
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.theme.colors.txtMuted};
  text-align: center;
  
  i {
    font-size: 3rem;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  h4 {
    margin: 0 0 8px 0;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const DetalleEquipo = ({ equipo, initialTab, onTabApplied }) => {
  // Acceso al theme para utilizarlo en estilos inline coherentes con el sistema de diseño
  const theme = useTheme();
  const { toast } = useToast();
  const { confirmar } = useConfirm();
  const { entrada } = useEntrada();
  const CONTROL_HEIGHT = 30;
  const compactIconButtonStyle = {
    height: CONTROL_HEIGHT,
    minHeight: CONTROL_HEIGHT,
    minWidth: 30,
    padding: '0 10px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `1px solid ${theme.colors.borderDefault}`,
    background: theme.colors.transparent,
    color: theme.colors.txtSecondary,
    cursor: 'pointer',
    fontWeight: 600,
    lineHeight: 1,
  };
  const compactActionButtonStyle = {
    height: CONTROL_HEIGHT,
    minHeight: CONTROL_HEIGHT,
    padding: '0 12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const [activeTab, setActiveTab] = useState('info');
  const navigate = useNavigate();

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      if (onTabApplied) onTabApplied();
    }
  }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [planAsignado, setPlanAsignado] = useState(null);
  const [rutinas, setRutinas] = useState([]);
  const [repuestosRecomendados, setRepuestosRecomendados] = useState([]);
  const [componentesOpts, setComponentesOpts] = useState([]);
  const [componenteIdSel, setComponenteIdSel] = useState('');
  const [componenteQuery, setComponenteQuery] = useState('');
  const [repuestosPorComponente, setRepuestosPorComponente] = useState([]);
  const [repuestoSel, setRepuestoSel] = useState('');
  const [repuestoQuery, setRepuestoQuery] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [savingRepuesto, setSavingRepuesto] = useState(false);
  const [showClonadoModal, setShowClonadoModal] = useState(false);
  const [loadingEquiposClonacion, setLoadingEquiposClonacion] = useState(false);
  const [equiposParaClonar, setEquiposParaClonar] = useState([]);
  const [equipoOrigenClonadoSel, setEquipoOrigenClonadoSel] = useState('');
  const [equipoOrigenClonadoQuery, setEquipoOrigenClonadoQuery] = useState('');
  // Dirección del clonado: 'traer' = desde otro equipo hacia este;
  // 'enviar' = desde este equipo hacia uno o varios equipos.
  const [modoClonado, setModoClonado] = useState('traer');
  const [equiposDestinoSel, setEquiposDestinoSel] = useState([]);
  const [destinosBusqueda, setDestinosBusqueda] = useState('');
  const [loadingRepuestosOrigen, setLoadingRepuestosOrigen] = useState(false);
  const [repuestosOrigen, setRepuestosOrigen] = useState([]);
  const [selectedCloneIds, setSelectedCloneIds] = useState([]);
  const [clonandoRepuestos, setClonandoRepuestos] = useState(false);

  // Sub-pestaña dentro de "Repuestos": recomendados | capacidades de llenado (fluidos)
  const [repuestosSubtab, setRepuestosSubtab] = useState('recomendados');
  const [capacidadesLlenado, setCapacidadesLlenado] = useState([]);
  const [fluidosOpts, setFluidosOpts] = useState([]);
  const [combustiblesOpts, setCombustiblesOpts] = useState([]);
  const [depositosOpts, setDepositosOpts] = useState([]);
  const [capDepositoSel, setCapDepositoSel] = useState('');
  const [capDepositoQuery, setCapDepositoQuery] = useState('');
  const [capItemSel, setCapItemSel] = useState(''); // clave combinada: "fluido:<id>" o "combustible:<id>"
  const [capItemQuery, setCapItemQuery] = useState('');
  const [capLitros, setCapLitros] = useState('');
  const [savingCapacidad, setSavingCapacidad] = useState(false);
  const [showClonadoCapModal, setShowClonadoCapModal] = useState(false);
  const [capacidadesOrigen, setCapacidadesOrigen] = useState([]);
  const [loadingCapacidadesOrigen, setLoadingCapacidadesOrigen] = useState(false);
  const [selectedCloneCapIds, setSelectedCloneCapIds] = useState([]);
  const [clonandoCapacidades, setClonandoCapacidades] = useState(false);
  const [showModalDeposito, setShowModalDeposito] = useState(false);
  const [showModalCombustible, setShowModalCombustible] = useState(false);
  const [mantenimientoLoading, setMantenimientoLoading] = useState(false);
  const [mantenimientoProximos, setMantenimientoProximos] = useState([]);
  const [mantenimientoHistorial, setMantenimientoHistorial] = useState([]);
  const [mantenimientoSortColumn, setMantenimientoSortColumn] = useState('odometro_programado');
  const [mantenimientoSortDirection, setMantenimientoSortDirection] = useState('desc');
  const [mantenimientoPage, setMantenimientoPage] = useState(1);
  const [mantenimientoPageSize, setMantenimientoPageSize] = useState(10);
  const [showCambioContador, setShowCambioContador] = useState(false);
  const [loadingCambiosContador, setLoadingCambiosContador] = useState(false);
  const [savingCambioContador, setSavingCambioContador] = useState(false);
  const [cambiosContador, setCambiosContador] = useState([]);
  const [resumenContador, setResumenContador] = useState({ odometro_actual: null, total_trabajado: null });
  const [horasHeredadas, setHorasHeredadas] = useState(null);
  // El equipo llega desde el árbol (EquipoArbolSerializer, sin campos calculados
  // para evitar N+1 en el listado): la vinculación se trae aparte, solo para el
  // equipo que está mostrándose en el detalle.
  const [vinculacion, setVinculacion] = useState({ montaje_vigente: null, implementos_montados_vigentes: [] });
  const [formCambioContador, setFormCambioContador] = useState({
    fecha_cambio: '',
    lectura_salida: '',
    lectura_inicial_nueva: '0',
    observaciones: '',
    anclar_mantenimiento: true,
    rutina_inicial: ''
  });
  const [showConfig, setShowConfig] = useState(false);
  const [showPlanEquipo, setShowPlanEquipo] = useState(false);
  const [showParteModal, setShowParteModal] = useState(false);
  const [editingParte, setEditingParte] = useState(null);
  const [showLubricanteModal, setShowLubricanteModal] = useState(false);
  const [editingLubricante, setEditingLubricante] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDocumento, setEditingDocumento] = useState(null);
  const [documentoAdjuntos, setDocumentoAdjuntos] = useState(null);
  const [showEditRepuestoModal, setShowEditRepuestoModal] = useState(false);
  const [editingRepuesto, setEditingRepuesto] = useState(null);
  const [editCantidadRepuesto, setEditCantidadRepuesto] = useState('1');
  const [savingEditRepuesto, setSavingEditRepuesto] = useState(false);
  const [showEditCapacidadModal, setShowEditCapacidadModal] = useState(false);
  const [editingCapacidad, setEditingCapacidad] = useState(null);
  const [editCapacidadLitros, setEditCapacidadLitros] = useState('0');
  const [savingEditCapacidad, setSavingEditCapacidad] = useState(false);
  const [showModalComponente, setShowModalComponente] = useState(false);
  const [showModalRepuesto, setShowModalRepuesto] = useState(false);
  const [showMapaGPS, setShowMapaGPS] = useState(false);
  const cambioBackdropGuard = useBackdropCloseGuard();
  const editRepuestoBackdropGuard = useBackdropCloseGuard();
  const editCapacidadBackdropGuard = useBackdropCloseGuard();
  const clonadoBackdropGuard = useBackdropCloseGuard();
  const { resetBackdropGuard: resetClonadoBackdropGuard } = clonadoBackdropGuard;
  const listaPartesRef = useRef();
  const listaOtcRef = useRef();
  const listaLubricantesRef = useRef();
  const listaDocumentacionRef = useRef();
  const listaRepuestosRecomendadosRef = useRef();
  const listaCapacidadesRef = useRef();

  const refreshLista = (ref) => {
    if (ref.current) {
      ref.current.refreshImmediate();
    }
  };

  const handleDeleteParte = async (parte) => {
    const ok = await confirmar({ titulo: '¿Eliminar este parte diario?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return true;
    await apiClient.delete(`partes-diarios/${parte.id}/`);
    refreshLista(listaPartesRef);
    return true;
  };

  const handleCancelarOtc = async (row) => {
    if (!row || row.estado !== 'abierta') return true;
    const ok = await confirmar({ titulo: `¿Cancelar OTC ${row.numero_ot || ''}?`, mensaje: 'La OTC quedará bloqueada para edición.', labelConfirmar: 'Sí, cancelar', variante: 'danger' });
    if (!ok) return true;
    await otcService.cancelar(row.id, { fecha_cierre: hoyArgentina() });
    refreshLista(listaOtcRef);
    return true;
  };

  const handleDeleteLubricante = async (registro) => {
    const ok = await confirmar({ titulo: '¿Eliminar este registro de lubricante?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return true;
    await apiClient.delete(`lubricantes/${registro.id}/`);
    refreshLista(listaLubricantesRef);
    return true;
  };

  const handleDeleteDocumento = async (documento) => {
    const ok = await confirmar({ titulo: '¿Eliminar este documento?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return true;
    await apiClient.delete(`documentacion/${documento.id}/`);
    refreshLista(listaDocumentacionRef);
    return true;
  };

  useEffect(() => {
    if (!equipo?.id) {
      setResumenContador({ odometro_actual: null, total_trabajado: null });
      return;
    }
    setResumenContador({
      odometro_actual: equipo.odometro_actual ?? equipo.odometro ?? 0,
      total_trabajado: equipo.total_trabajado ?? equipo.odometro_actual ?? equipo.odometro ?? 0,
    });
  }, [equipo?.id, equipo?.odometro_actual, equipo?.odometro, equipo?.total_trabajado]);

  // Cargar Plan Asignado y Rutinas para el equipo seleccionado.
  // Extraído del useEffect a un callback propio para poder recargarlo tras
  // asignar, cambiar o quitar el plan desde el modal de gestión.
  const recargarPlanAsignado = useCallback(async () => {
    try {
      if (!equipo?.id) return;
      const asignados = await planService.obtenerAsignados({ equipo: equipo.id });
      const pa = (asignados.results || asignados).find(a => String(a.equipo) === String(equipo.id));
      setPlanAsignado(pa || null);
      if (pa?.plan) {
        try {
          const rs = await planService.obtenerRutinasPorPlan(pa.plan);
          setRutinas(rs.results || rs || []);
        } catch {}
      } else {
        setRutinas([]);
      }
    } catch (e) {
      console.warn('No se pudo cargar plan asignado para el equipo', e);
    }
  }, [equipo?.id]);

  useEffect(() => { recargarPlanAsignado(); }, [recargarPlanAsignado]);

  // Cargar repuestos recomendados para el equipo
  const obtenerRepuestosRecomendadosEquipo = useCallback(async (equipoId) => {
    if (!equipoId) return [];
    let nextUrl = `/repuestos-recomendados/?equipo=${equipoId}&page_size=1000`;
    let acumulado = [];
    let guard = 0;

    while (nextUrl && guard < 30) {
      const resp = await apiClient.get(nextUrl);
      const data = resp.data;
      if (Array.isArray(data)) {
        acumulado = acumulado.concat(data);
        nextUrl = null;
      } else {
        const results = Array.isArray(data?.results) ? data.results : [];
        acumulado = acumulado.concat(results);
        nextUrl = data?.next || null;
      }
      guard += 1;
    }

    return acumulado;
  }, []);

  const cargarRepuestosRecomendados = useCallback(async (equipoId) => {
    if (!equipoId) return;
    try {
      const acumulado = await obtenerRepuestosRecomendadosEquipo(equipoId);
      setRepuestosRecomendados(acumulado);
    } catch (err) {
      console.error('Error cargando repuestos recomendados', err);
      setRepuestosRecomendados([]);
    }
    // La lista visible vive en ListaUnificada (fetch propio por endpoint); esto solo
    // le pide que vuelva a pedir sus datos, no le pasa el array de arriba.
    refreshLista(listaRepuestosRecomendadosRef);
  }, [obtenerRepuestosRecomendadosEquipo]);

  const cargarCapacidadesLlenado = useCallback(async (equipoId) => {
    if (!equipoId) return;
    try {
      const data = await equiposService.obtenerCapacidadesLlenado(equipoId);
      setCapacidadesLlenado(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error cargando capacidades de llenado', err);
      setCapacidadesLlenado([]);
    }
    refreshLista(listaCapacidadesRef);
  }, []);

  // Trae los repuestos-fluido (tipo_asignacion='deposito') vinculados al depósito
  // elegido. Sin depósito seleccionado no hay fluidos para ofrecer: no tiene sentido
  // mostrar el catálogo completo de fluidos de todos los depósitos del equipo.
  const cargarFluidos = useCallback(async (depositoId) => {
    if (!depositoId) {
      setFluidosOpts([]);
    } else {
      try {
        const resp = await apiClient.get(`repuestos/?deposito=${depositoId}&tipo_asignacion=deposito&page_size=9999`);
        const list = resp.data?.results || resp.data || [];
        setFluidosOpts(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error cargando fluidos del depósito', err);
        setFluidosOpts([]);
      }
    }
    // Los tipos de combustible solo aplican al tanque de combustible del equipo:
    // sin ese depósito seleccionado no tiene sentido ofrecerlos como opción.
    const depositoSeleccionado = (depositosOpts || []).find((d) => String(d.id) === String(depositoId));
    if (!depositoSeleccionado?.es_tanque_combustible) {
      setCombustiblesOpts([]);
      return;
    }
    try {
      const resp = await apiClient.get('tipos-combustible/?page_size=9999');
      const list = resp.data?.results || resp.data || [];
      setCombustiblesOpts(Array.isArray(list) ? list.filter(c => c.activo !== false) : []);
    } catch (err) {
      console.error('Error cargando tipos de combustible', err);
      setCombustiblesOpts([]);
    }
  }, [depositosOpts]);

  const cargarDepositos = useCallback(async () => {
    try {
      const list = await equiposService.obtenerDepositos();
      setDepositosOpts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error cargando depósitos', err);
      setDepositosOpts([]);
    }
  }, []);

  // Unidad de litros según el catálogo del tenant: se toma de cualquier capacidad
  // de combustible ya cargada (el backend ya la resuelve ahí vía unidad_medida_litros_display).
  // Si el equipo todavía no tiene ninguna capacidad de combustible cargada, cae a 'L'.
  const unidadLitrosDetectada = React.useMemo(() => (
    (capacidadesLlenado || []).find((c) => c.es_combustible)?.fluido_unidad_medida || 'L'
  ), [capacidadesLlenado]);

  // Lista combinada para el selector único de "Fluido / Combustible" de capacidades de llenado
  const capItemsOpts = React.useMemo(() => {
    const fluidos = (fluidosOpts || []).map(f => ({
      key: `fluido:${f.id}`,
      tipo: 'fluido',
      id: f.id,
      label: [f.codigo, f.marca_nombre, f.descripcion].filter(Boolean).join(' - ') || `Fluido ${f.id}`,
      unidadMedida: f.unidad_medida_abreviatura || f.unidad_medida_nombre || '',
    }));
    const combustibles = (combustiblesOpts || []).map(c => ({
      key: `combustible:${c.id}`,
      tipo: 'combustible',
      id: c.id,
      label: c.nombre,
      unidadMedida: unidadLitrosDetectada,
    }));
    return [...fluidos, ...combustibles];
  }, [fluidosOpts, combustiblesOpts, unidadLitrosDetectada]);

  // Unidad de medida del fluido/combustible elegido en el formulario de alta de capacidad
  const capItemUnidad = React.useMemo(() => {
    const item = (capItemsOpts || []).find((it) => it.key === capItemSel);
    return item?.unidadMedida || 'L';
  }, [capItemsOpts, capItemSel]);


  const guardarCapacidad = async () => {
    if (!capItemSel) { toast.warning('Seleccione un fluido o combustible.'); return; }
    if (capLitros === '' || Number(capLitros) <= 0) { toast.warning('Ingrese una capacidad válida.'); return; }

    const [itemTipo, itemIdRaw] = String(capItemSel).split(':');
    const itemId = Number(itemIdRaw);

    // Prevenir duplicados (equipo + deposito + fluido/combustible)
    const selDep = capDepositoSel && capDepositoSel !== '' ? String(capDepositoSel) : null;
    const existe = (capacidadesLlenado || []).some(c => {
      const mismoDep = String(c.deposito ?? null) === String(selDep);
      if (itemTipo === 'combustible') {
        return mismoDep && String(c.tipo_combustible) === String(itemId);
      }
      return mismoDep && String(c.fluido) === String(itemId);
    });
    if (existe) {
      toast.warning('Ya existe una capacidad para ese fluido/combustible y depósito en este equipo.');
      return;
    }

    setSavingCapacidad(true);
    try {
      await equiposService.crearCapacidadLlenado({
        equipo: equipo.id,
        deposito: capDepositoSel || null,
        fluido: itemTipo === 'fluido' ? itemId : null,
        tipo_combustible: itemTipo === 'combustible' ? itemId : null,
        capacidad_litros: Number(capLitros),
      });
      setCapDepositoSel('');
      setCapDepositoQuery('');
      setCapItemSel('');
      setCapItemQuery('');
      setCapLitros('');
      await cargarCapacidadesLlenado(equipo.id);
    } catch (err) {
      console.error('Error asignando capacidad de llenado', err);
      toast.error(err?.response?.data?.detail || 'No se pudo asignar la capacidad. Intentá nuevamente.');
    } finally {
      setSavingCapacidad(false);
    }
  };

  const eliminarCapacidad = async (id) => {
    const ok = await confirmar({ titulo: '¿Eliminar capacidad de llenado?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return true;
    try {
      await equiposService.eliminarCapacidadLlenado(id);
      await cargarCapacidadesLlenado(equipo.id);
    } catch (err) {
      console.error('Error eliminando capacidad de llenado', err);
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar la capacidad. Intentá nuevamente.');
    }
    return true;
  };

  const eliminarRepuestoRecomendado = async (rr) => {
    const ok = await confirmar({ titulo: '¿Eliminar repuesto recomendado?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return true;
    try {
      await apiClient.delete(`repuestos-recomendados/${rr.id}/`);
      await cargarRepuestosRecomendados(equipo.id);
    } catch (err) {
      console.error('Error eliminando repuesto recomendado', err);
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar el repuesto. Intentá nuevamente.');
    }
    return true;
  };

  const editarCapacidadLitros = (cap) => {
    setEditingCapacidad(cap);
    setEditCapacidadLitros(String(Number(cap?.capacidad_litros || 0)));
    setShowEditCapacidadModal(true);
  };

  const guardarEdicionCapacidad = async () => {
    if (!editingCapacidad?.id) return;

    const nuevaCapacidad = Number(editCapacidadLitros);
    if (!Number.isFinite(nuevaCapacidad) || nuevaCapacidad <= 0) {
      toast.warning('Ingrese una capacidad válida.');
      return;
    }

    const actual = Number(editingCapacidad?.capacidad_litros || 0);
    if (nuevaCapacidad === actual) {
      setShowEditCapacidadModal(false);
      setEditingCapacidad(null);
      return;
    }

    setSavingEditCapacidad(true);
    try {
      await equiposService.actualizarCapacidadLlenado(editingCapacidad.id, { capacidad_litros: nuevaCapacidad });
      await cargarCapacidadesLlenado(equipo.id);
      setShowEditCapacidadModal(false);
      setEditingCapacidad(null);
    } catch (err) {
      console.error('Error editando capacidad de llenado', err);
      toast.error(err?.response?.data?.detail || 'No se pudo editar la capacidad. Intentá nuevamente.');
    } finally {
      setSavingEditCapacidad(false);
    }
  };

  // Alta al vuelo de un depósito (botón +), autoselecciona el creado
  const handleDepositoRecordSaved = useCallback(async (savedRecord) => {
    await cargarDepositos();
    const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
    if (!nextId) return;
    setCapDepositoSel(nextId);
    setCapDepositoQuery(savedRecord?.nombre || savedRecord?.name || '');
  }, [cargarDepositos]);

  // Alta al vuelo de un tipo de combustible (botón +), autoselecciona el creado
  const handleCombustibleRecordSaved = useCallback(async (savedRecord) => {
    await cargarFluidos(capDepositoSel || null);
    const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
    if (!nextId) return;
    setCapItemSel(`combustible:${nextId}`);
    setCapItemQuery(savedRecord?.nombre || savedRecord?.name || '');
  }, [cargarFluidos, capDepositoSel]);

  // Clonado de capacidades entre equipos (traer desde otro / enviar a varios).
  // Si el equipo ya tiene capacidades cargadas, arranca en "enviar": es el caso
  // típico de replicar un equipo modelo sobre el resto de la flota igual.
  const abrirModalClonadoCap = async () => {
    const modoInicial = (capacidadesLlenado || []).length > 0 ? 'enviar' : 'traer';
    setShowClonadoCapModal(true);
    resetClonadoBackdropGuard();
    setModoClonado(modoInicial);
    setEquipoOrigenClonadoSel('');
    setEquipoOrigenClonadoQuery('');
    setCapacidadesOrigen([]);
    setEquiposDestinoSel([]);
    setDestinosBusqueda('');
    setSelectedCloneCapIds(modoInicial === 'enviar' ? (capacidadesLlenado || []).map((item) => item.id) : []);
    await cargarEquiposParaClonar();
  };

  const cargarCapacidadesOrigen = async (equipoOrigenId) => {
    if (!equipoOrigenId) {
      setCapacidadesOrigen([]);
      setSelectedCloneCapIds([]);
      return;
    }
    setLoadingCapacidadesOrigen(true);
    try {
      const list = await equiposService.obtenerCapacidadesLlenado(equipoOrigenId);
      setCapacidadesOrigen(Array.isArray(list) ? list : []);
      setSelectedCloneCapIds((Array.isArray(list) ? list : []).map((item) => item.id));
    } catch (err) {
      console.error('Error cargando capacidades del equipo origen', err);
      setCapacidadesOrigen([]);
      setSelectedCloneCapIds([]);
    } finally {
      setLoadingCapacidadesOrigen(false);
    }
  };

  const toggleSeleccionClonadoCap = (id) => {
    setSelectedCloneCapIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const buildClaveCapacidad = (item) => {
    const dep = item?.deposito == null ? 'null' : String(item.deposito);
    const flu = item?.fluido == null ? '' : String(item.fluido);
    const comb = item?.tipo_combustible == null ? '' : String(item.tipo_combustible);
    return `${dep}|${flu}|${comb}`;
  };

  const capacidadesDestinoSet = React.useMemo(
    () => new Set((capacidadesLlenado || []).map((item) => buildClaveCapacidad(item))),
    [capacidadesLlenado],
  );

  const obtenerRepuestosPorComponente = useCallback(async (componenteId) => {
    if (!componenteId) return [];
    let nextUrl = `/repuestos-por-componente/?componente=${componenteId}&page_size=1000`;
    let acumulado = [];
    let guard = 0;

    while (nextUrl && guard < 30) {
      const resp = await apiClient.get(nextUrl);
      const data = resp.data;
      if (Array.isArray(data)) {
        acumulado = acumulado.concat(data);
        nextUrl = null;
      } else {
        const results = Array.isArray(data?.results) ? data.results : [];
        acumulado = acumulado.concat(results);
        nextUrl = data?.next || null;
      }
      guard += 1;
    }

    return acumulado;
  }, []);

  const buildClaveRepuesto = (item) => {
    const comp = item?.componente == null || item?.componente === '' ? 'null' : String(item.componente);
    const rep = item?.repuesto == null ? '' : String(typeof item.repuesto === 'object' ? item.repuesto.id : item.repuesto);
    return `${comp}|${rep}`;
  };

  const cargarEquiposParaClonar = async () => {
    if (!equipo?.id) return;
    setLoadingEquiposClonacion(true);
    try {
      const response = await equiposService.getTodosEquipos();
      const todos = Array.isArray(response?.data) ? response.data : [];
      const candidatos = todos
        .filter((item) => String(item?.id) !== String(equipo.id))
        .sort((a, b) => String(a?.interno || '').localeCompare(String(b?.interno || '')));
      setEquiposParaClonar(candidatos);
    } catch (err) {
      console.error('Error cargando equipos para clonar repuestos', err);
      setEquiposParaClonar([]);
    } finally {
      setLoadingEquiposClonacion(false);
    }
  };

  const cargarRepuestosOrigen = async (equipoOrigenId) => {
    if (!equipoOrigenId) {
      setRepuestosOrigen([]);
      setSelectedCloneIds([]);
      return;
    }

    setLoadingRepuestosOrigen(true);
    try {
      const list = await obtenerRepuestosRecomendadosEquipo(equipoOrigenId);
      setRepuestosOrigen(list);
      setSelectedCloneIds(list.map((item) => item.id));
    } catch (err) {
      console.error('Error cargando repuestos del equipo origen', err);
      setRepuestosOrigen([]);
      setSelectedCloneIds([]);
    } finally {
      setLoadingRepuestosOrigen(false);
    }
  };

  // Clonado de repuestos recomendados entre equipos (traer desde otro / enviar a varios).
  const abrirModalClonado = async () => {
    const modoInicial = (repuestosRecomendados || []).length > 0 ? 'enviar' : 'traer';
    setShowClonadoModal(true);
    resetClonadoBackdropGuard();
    setModoClonado(modoInicial);
    setEquipoOrigenClonadoSel('');
    setEquipoOrigenClonadoQuery('');
    setRepuestosOrigen([]);
    setEquiposDestinoSel([]);
    setDestinosBusqueda('');
    setSelectedCloneIds(modoInicial === 'enviar' ? (repuestosRecomendados || []).map((item) => item.id) : []);
    await cargarEquiposParaClonar();
  };

  const labelEquipoClonado = (item) => (
    `${item?.interno || ''}${item?.descripcion ? ` - ${item.descripcion}` : ''}${item?.marca_nombre ? ` | ${item.marca_nombre}` : ''}${item?.modelo_nombre ? ` / ${item.modelo_nombre}` : ''}`.trim()
  );

  // Cambio de dirección del clonado. Cada modo trabaja con una lista distinta
  // (el equipo origen elegido vs. lo que ya tiene este equipo), así que la
  // selección se rearma de cero al cambiar.
  const cambiarModoClonado = (siguienteModo, tipo) => {
    if (siguienteModo === modoClonado) return;
    setModoClonado(siguienteModo);
    setEquipoOrigenClonadoSel('');
    setEquipoOrigenClonadoQuery('');
    setEquiposDestinoSel([]);
    setDestinosBusqueda('');

    if (tipo === 'capacidades') {
      setCapacidadesOrigen([]);
      setSelectedCloneCapIds(siguienteModo === 'enviar' ? (capacidadesLlenado || []).map((item) => item.id) : []);
      return;
    }

    setRepuestosOrigen([]);
    setSelectedCloneIds(siguienteModo === 'enviar' ? (repuestosRecomendados || []).map((item) => item.id) : []);
  };

  const toggleSeleccionClonado = (id) => {
    setSelectedCloneIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const seleccionarTodosClonado = () => {
    const base = modoClonado === 'enviar' ? (repuestosRecomendados || []) : (repuestosOrigen || []);
    setSelectedCloneIds(base.map((item) => item.id));
  };

  const limpiarSeleccionClonado = () => {
    setSelectedCloneIds([]);
  };

  // ---- Selección de equipos destino (modo "enviar") ----

  const toggleEquipoDestino = (id) => {
    const clave = String(id);
    setEquiposDestinoSel((prev) => (
      prev.includes(clave) ? prev.filter((x) => x !== clave) : [...prev, clave]
    ));
  };

  const equiposDestinoFiltrados = React.useMemo(() => {
    const termino = String(destinosBusqueda || '').trim().toLowerCase();
    if (!termino) return equiposParaClonar || [];
    return (equiposParaClonar || []).filter((item) => (
      `${labelEquipoClonado(item)} ${item?.interno_alternativo || ''}`.toLowerCase().includes(termino)
    ));
  }, [equiposParaClonar, destinosBusqueda]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atajos para el caso real: "cargué el P440 y quiero replicarlo en todos los P440".
  const atajosDestino = React.useMemo(() => {
    const definiciones = [
      { campo: 'modelo', etiqueta: 'Mismo modelo', nombre: equipo?.modelo_nombre },
      { campo: 'marca', etiqueta: 'Misma marca', nombre: equipo?.marca_nombre },
      { campo: 'tipo', etiqueta: 'Mismo tipo', nombre: equipo?.tipo_nombre },
      { campo: 'familia', etiqueta: 'Misma familia', nombre: equipo?.familia_nombre },
    ];

    return definiciones.reduce((acumulado, definicion) => {
      const valor = equipo?.[definicion.campo];
      if (valor == null || valor === '') return acumulado;
      const coincidencias = (equiposParaClonar || []).filter((item) => String(item?.[definicion.campo]) === String(valor));
      if (coincidencias.length === 0) return acumulado;
      acumulado.push({
        clave: definicion.campo,
        etiqueta: definicion.nombre ? `${definicion.etiqueta}: ${definicion.nombre}` : definicion.etiqueta,
        ids: coincidencias.map((item) => String(item.id)),
      });
      return acumulado;
    }, []);
  }, [equipo, equiposParaClonar]);

  const aplicarAtajoDestino = (ids) => {
    setEquiposDestinoSel((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const renderModoClonado = (tipo) => {
    const opciones = [
      { valor: 'traer', etiqueta: 'Traer desde otro equipo' },
      { valor: 'enviar', etiqueta: 'Copiar a otros equipos' },
    ];
    return (
      <div style={{ display: 'inline-flex', border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 8, overflow: 'hidden' }}>
        {opciones.map((opcion) => {
          const activo = modoClonado === opcion.valor;
          return (
            <button
              key={opcion.valor}
              onClick={() => cambiarModoClonado(opcion.valor, tipo)}
              style={{
                background: activo ? theme.colors.primaryAlpha : theme.colors.transparent,
                color: activo ? theme.colors.primary : theme.colors.txtSecondary,
                border: 'none',
                padding: '7px 12px',
                fontSize: 13,
                fontWeight: activo ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {opcion.etiqueta}
            </button>
          );
        })}
      </div>
    );
  };

  const clonadoChipStyle = {
    background: theme.colors.transparent,
    color: theme.colors.txtPrincipal,
    border: `1px solid ${theme.colors.borderDefault}`,
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
  };

  const renderPanelDestinos = () => (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: 10, borderBottom: `1px solid ${theme.colors.borderDefault}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{ color: theme.colors.txtPrincipal, fontSize: 13 }}>Equipos destino</strong>
          <span style={{ color: theme.colors.txtSecondary, fontSize: 12 }}>{equiposDestinoSel.length} seleccionado(s)</span>
        </div>

        <input
          value={destinosBusqueda}
          onChange={(event) => setDestinosBusqueda(event.target.value)}
          placeholder="Buscar por interno, marca o modelo..."
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${theme.colors.borderDefault}`, background: theme.colors.bgPrincipal, color: theme.colors.txtPrincipal, fontSize: 13 }}
        />

        {atajosDestino.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {atajosDestino.map((atajo) => (
              <button
                key={atajo.clave}
                onClick={() => aplicarAtajoDestino(atajo.ids)}
                title={`Agregar ${atajo.ids.length} equipo(s) a la selección`}
                style={clonadoChipStyle}
              >
                {atajo.etiqueta} ({atajo.ids.length})
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => aplicarAtajoDestino(equiposDestinoFiltrados.map((item) => String(item.id)))}
            disabled={equiposDestinoFiltrados.length === 0}
            style={clonadoChipStyle}
          >
            Seleccionar visibles
          </button>
          <button
            onClick={() => setEquiposDestinoSel([])}
            disabled={equiposDestinoSel.length === 0}
            style={clonadoChipStyle}
          >
            Limpiar
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {loadingEquiposClonacion ? (
          <div style={{ padding: 10, color: theme.colors.txtSecondary, fontSize: 13 }}>Cargando equipos...</div>
        ) : equiposDestinoFiltrados.length === 0 ? (
          <div style={{ padding: 10, color: theme.colors.txtSecondary, fontSize: 13 }}>Sin equipos que coincidan con la búsqueda.</div>
        ) : (
          equiposDestinoFiltrados.map((item) => {
            const clave = String(item.id);
            const checked = equiposDestinoSel.includes(clave);
            const detalle = [item.descripcion, item.marca_nombre, item.modelo_nombre].filter(Boolean).join(' · ');
            return (
              <label
                key={clave}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '7px 10px',
                  borderBottom: `1px solid ${theme.colors.bgSecundario}`,
                  background: checked ? theme.colors.primaryAlpha : theme.colors.transparent,
                  cursor: 'pointer',
                }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggleEquipoDestino(clave)} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', color: theme.colors.txtPrincipal, fontSize: 13, fontWeight: 600 }}>{item.interno || '-'}</span>
                  <span style={{ display: 'block', color: theme.colors.txtSecondary, fontSize: 12 }}>{detalle || 'Sin datos adicionales'}</span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );

  // Envío del clonado. El backend acepta un destino o varios: en modo "traer"
  // el destino es este equipo, en modo "enviar" son los equipos tildados.
  const ejecutarClonadoRepuestos = async () => {
    if (modoClonado === 'traer' && !equipoOrigenClonadoSel) {
      toast.warning('Seleccione un equipo origen.');
      return;
    }
    if (modoClonado === 'enviar' && equiposDestinoSel.length === 0) {
      toast.warning('Seleccione al menos un equipo destino.');
      return;
    }
    if (selectedCloneIds.length === 0) {
      toast.warning('Seleccione al menos un repuesto para clonar.');
      return;
    }

    if (modoClonado === 'enviar' && equiposDestinoSel.length > 1) {
      const ok = await confirmar({
        titulo: `¿Copiar ${selectedCloneIds.length} repuesto(s) a ${equiposDestinoSel.length} equipos?`,
        mensaje: 'Los repuestos que ya estén cargados en cada equipo se omiten.',
        labelConfirmar: 'Sí, copiar',
      });
      if (!ok) return;
    }

    setClonandoRepuestos(true);
    try {
      const payload = modoClonado === 'enviar'
        ? {
          equipo_origen: equipo.id,
          equipos_destino: equiposDestinoSel.map((id) => Number(id)),
          recomendaciones_ids: selectedCloneIds.map((id) => Number(id)),
        }
        : {
          equipo_origen: Number(equipoOrigenClonadoSel),
          equipo_destino: equipo.id,
          recomendaciones_ids: selectedCloneIds.map((id) => Number(id)),
        };

      const data = await equiposService.clonarRepuestosRecomendados(payload);
      const creados = Number(data?.creados || 0);
      const omitidos = Number(data?.omitidos || 0);

      if (modoClonado === 'traer') {
        await cargarRepuestosRecomendados(equipo.id);
      }
      setShowClonadoModal(false);

      toast.success(modoClonado === 'enviar'
        ? `Se copiaron ${creados} repuesto(s) a ${equiposDestinoSel.length} equipo(s). Se omitieron ${omitidos} que ya estaban cargados.`
        : `Clonación completada. Se agregaron ${creados} repuesto(s) y se omitieron ${omitidos} duplicado(s).`);
    } catch (err) {
      console.error('Error clonando repuestos', err);
      toast.error(err?.response?.data?.detail || 'No se pudo copiar los repuestos. Intentá nuevamente.');
    } finally {
      setClonandoRepuestos(false);
    }
  };

  const ejecutarClonadoCapacidades = async () => {
    if (modoClonado === 'traer' && !equipoOrigenClonadoSel) {
      toast.warning('Seleccione un equipo origen.');
      return;
    }
    if (modoClonado === 'enviar' && equiposDestinoSel.length === 0) {
      toast.warning('Seleccione al menos un equipo destino.');
      return;
    }
    if (selectedCloneCapIds.length === 0) {
      toast.warning('Seleccione al menos una capacidad para clonar.');
      return;
    }

    if (modoClonado === 'enviar' && equiposDestinoSel.length > 1) {
      const ok = await confirmar({
        titulo: `¿Copiar ${selectedCloneCapIds.length} depósito(s) a ${equiposDestinoSel.length} equipos?`,
        mensaje: 'Los depósitos que ya estén cargados en cada equipo se omiten.',
        labelConfirmar: 'Sí, copiar',
      });
      if (!ok) return;
    }

    setClonandoCapacidades(true);
    try {
      const payload = modoClonado === 'enviar'
        ? {
          equipo_origen: equipo.id,
          equipos_destino: equiposDestinoSel.map((id) => Number(id)),
          capacidades_ids: selectedCloneCapIds.map((id) => Number(id)),
        }
        : {
          equipo_origen: Number(equipoOrigenClonadoSel),
          equipo_destino: equipo.id,
          capacidades_ids: selectedCloneCapIds.map((id) => Number(id)),
        };

      const data = await equiposService.clonarCapacidadesLlenado(payload);
      const creados = Number(data?.creados || 0);
      const omitidos = Number(data?.omitidos || 0);

      if (modoClonado === 'traer') {
        await cargarCapacidadesLlenado(equipo.id);
      }
      setShowClonadoCapModal(false);

      toast.success(modoClonado === 'enviar'
        ? `Se copiaron ${creados} depósito(s) a ${equiposDestinoSel.length} equipo(s). Se omitieron ${omitidos} que ya estaban cargados.`
        : `Clonación completada. Se agregaron ${creados} capacidad(es) y se omitieron ${omitidos} duplicado(s).`);
    } catch (err) {
      console.error('Error clonando capacidades', err);
      toast.error(err?.response?.data?.detail || 'No se pudo copiar las capacidades. Intentá nuevamente.');
    } finally {
      setClonandoCapacidades(false);
    }
  };

  const editarCantidadRepuesto = (rr) => {
    setEditingRepuesto(rr);
    setEditCantidadRepuesto(String(Number(rr?.cantidad || 1)));
    setShowEditRepuestoModal(true);
  };

  const guardarEdicionRepuesto = async () => {
    if (!editingRepuesto?.id) return;

    const nuevaCantidad = Number(editCantidadRepuesto);
    if (!Number.isInteger(nuevaCantidad) || nuevaCantidad < 1) {
      toast.warning('La cantidad debe ser un número entero mayor o igual a 1.');
      return;
    }

    const actual = Number(editingRepuesto?.cantidad || 1);
    if (nuevaCantidad === actual) {
      setShowEditRepuestoModal(false);
      setEditingRepuesto(null);
      return;
    }

    setSavingEditRepuesto(true);
    try {
      await apiClient.patch(`repuestos-recomendados/${editingRepuesto.id}/`, { cantidad: nuevaCantidad });
      await cargarRepuestosRecomendados(equipo.id);
      setShowEditRepuestoModal(false);
      setEditingRepuesto(null);
    } catch (err) {
      console.error('Error editando repuesto recomendado', err);
      toast.error(err?.response?.data?.detail || 'No se pudo editar la recomendación. Intentá nuevamente.');
    } finally {
      setSavingEditRepuesto(false);
    }
  };

  const repuestosDestinoSet = React.useMemo(
    () => new Set((repuestosRecomendados || []).map((item) => buildClaveRepuesto(item))),
    [repuestosRecomendados],
  );

  // En modo "enviar" lo que se clona es lo que ya tiene este equipo;
  // en modo "traer", lo que se cargó del equipo origen elegido.
  const itemsClonadoRepuestos = React.useMemo(
    () => (modoClonado === 'enviar' ? (repuestosRecomendados || []) : (repuestosOrigen || [])),
    [modoClonado, repuestosRecomendados, repuestosOrigen],
  );
  const itemsClonadoCapacidades = React.useMemo(
    () => (modoClonado === 'enviar' ? (capacidadesLlenado || []) : (capacidadesOrigen || [])),
    [modoClonado, capacidadesLlenado, capacidadesOrigen],
  );

  const resumenClonado = React.useMemo(() => {
    const seleccionados = itemsClonadoRepuestos.filter((item) => selectedCloneIds.includes(item.id));
    // Los duplicados solo se pueden anticipar cuando el destino es este equipo.
    const duplicados = modoClonado === 'enviar'
      ? 0
      : seleccionados.filter((item) => repuestosDestinoSet.has(buildClaveRepuesto(item))).length;
    return {
      totalOrigen: itemsClonadoRepuestos.length,
      seleccionados: seleccionados.length,
      duplicados,
    };
  }, [itemsClonadoRepuestos, selectedCloneIds, repuestosDestinoSet, modoClonado]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar componentes (para selector)
  const cargarComponentes = useCallback(async () => {
    try {
      const resp = await apiClient.get('componentes/');
      const list = resp.data.results || resp.data || [];
      setComponentesOpts(list);
    } catch (err) {
      console.error('Error cargando componentes', err);
      setComponentesOpts([]);
    }
  }, []);

  const handleComponenteRecordSaved = useCallback(async (savedRecord) => {
    await cargarComponentes();
    const nextId = savedRecord?.id != null ? String(savedRecord.id) : '';
    if (!nextId) return;
    setComponenteIdSel(nextId);
    setComponenteQuery(savedRecord?.nombre || savedRecord?.name || '');
    setRepuestoSel('');
    setRepuestoQuery('');
  }, [cargarComponentes]);

  const handleRepuestoRecordSaved = useCallback(async (savedRecord) => {
    const createdId = savedRecord?.id != null ? String(savedRecord.id) : '';
    const createdComp = savedRecord?.componente
      ? String(typeof savedRecord.componente === 'object' ? savedRecord.componente.id : savedRecord.componente)
      : (Array.isArray(savedRecord?.componentes) && savedRecord.componentes.length > 0
        ? String(savedRecord.componentes[0]?.id ?? savedRecord.componentes[0])
        : '');

    const targetComp = componenteIdSel || createdComp;
    if (targetComp) {
      if (!componenteIdSel) {
        const selected = (componentesOpts || []).find(c => String(c.id) === String(targetComp));
        setComponenteIdSel(String(targetComp));
        setComponenteQuery(selected?.nombre || selected?.name || '');
      }
      try {
        const list = await obtenerRepuestosPorComponente(targetComp);
        setRepuestosPorComponente(list);
      } catch (err) {
        console.error('Error cargando repuestos por componente luego de alta', err);
      }
    }

    if (createdId) {
      setRepuestoSel(createdId);
      setRepuestoQuery(`${savedRecord?.codigo || ''}${savedRecord?.descripcion ? ` - ${savedRecord.descripcion}` : ''}`.trim());
    }
  }, [componenteIdSel, componentesOpts, obtenerRepuestosPorComponente]);

  // Cuando cambia el componente seleccionado, cargar repuestos asociados
  useEffect(() => {
    const load = async () => {
      if (!componenteIdSel) {
        setRepuestosPorComponente([]);
        setRepuestoSel('');
        setRepuestoQuery('');
        return;
      }
      try {
        const list = await obtenerRepuestosPorComponente(componenteIdSel);
        setRepuestosPorComponente(list);
      } catch (err) {
        console.error('Error cargando repuestos por componente', err);
        setRepuestosPorComponente([]);
      }
    };
    load();
  }, [componenteIdSel, obtenerRepuestosPorComponente]);

  // Precargar datos cuando se abre la pestaña de repuestos
  useEffect(() => {
    // Resetear estado al cambiar equipo para evitar mostrar datos del anterior
    setRepuestosRecomendados([]);
    setRepuestosPorComponente([]);
    setComponenteIdSel('');
    setComponenteQuery('');
    setRepuestoSel('');
    setRepuestoQuery('');
    setCantidad(1);
    setShowClonadoModal(false);
    setEquiposParaClonar([]);
    setEquipoOrigenClonadoSel('');
    setEquipoOrigenClonadoQuery('');
    setRepuestosOrigen([]);
    setSelectedCloneIds([]);
    setModoClonado('traer');
    setEquiposDestinoSel([]);
    setDestinosBusqueda('');
    resetClonadoBackdropGuard();
    setShowEditRepuestoModal(false);
    setEditingRepuesto(null);
    setEditCantidadRepuesto('1');
    setSavingEditRepuesto(false);

    if (activeTab === 'repuestos' && equipo?.id) {
      cargarComponentes();
      cargarRepuestosRecomendados(equipo.id);
      cargarCapacidadesLlenado(equipo.id);
      cargarDepositos();
    }
  }, [activeTab, equipo?.id, cargarComponentes, cargarRepuestosRecomendados, cargarCapacidadesLlenado, cargarDepositos, resetClonadoBackdropGuard]);

  // Vinculación (montaje_vigente / implementos_montados_vigentes): no viene en
  // la prop `equipo` porque el árbol usa EquipoArbolSerializer (sin campos
  // calculados, para evitar N+1 en el listado). Se trae aparte, solo para el
  // equipo que está mostrándose (tab info, card Vinculación).
  useEffect(() => {
    setVinculacion({ montaje_vigente: null, implementos_montados_vigentes: [] });
    setHorasHeredadas(null);
    if (activeTab !== 'info' || !equipo?.id) return;
    let cancelado = false;
    apiClient.get(`equipos/${equipo.id}/`)
      .then(({ data }) => {
        if (cancelado) return;
        setVinculacion({
          montaje_vigente: data?.montaje_vigente ?? null,
          implementos_montados_vigentes: data?.implementos_montados_vigentes ?? [],
        });
        if (data?.montaje_vigente?.hereda_horas) {
          apiClient.get(`equipos/${equipo.id}/horas-heredadas/`)
            .then(({ data: hh }) => { if (!cancelado) setHorasHeredadas(hh?.total_heredado ?? 0); })
            .catch(() => { if (!cancelado) setHorasHeredadas(null); });
        }
      })
      .catch(() => { if (!cancelado) setVinculacion({ montaje_vigente: null, implementos_montados_vigentes: [] }); });
    return () => { cancelado = true; };
  }, [activeTab, equipo?.id]);

  // El fluido elegible depende del depósito seleccionado en el formulario de alta:
  // sin depósito no hay fluidos para ofrecer (evita mostrar el catálogo completo).
  useEffect(() => {
    if (activeTab !== 'repuestos') return;
    cargarFluidos(capDepositoSel || null);
  }, [activeTab, capDepositoSel, cargarFluidos]);

  // Sin valor, la fecha de hoy en Argentina; con valor, el día calendario de
  // ese momento. En los dos casos por componentes locales y nunca por
  // toISOString(), que devuelve la fecha en UTC y adelanta un día de noche.
  const toYmd = (value) => (value === undefined || value === null
    ? hoyArgentina()
    : toLocalYMD(value instanceof Date ? value : new Date(value)));

  const cargarCambiosContador = async (equipoId) => {
    if (!equipoId) return;
    setLoadingCambiosContador(true);
    try {
      const resp = await apiClient.get(`/equipos/${equipoId}/cambios-contador/`);
      setCambiosContador(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error('Error cargando cambios de contador', err);
      setCambiosContador([]);
    } finally {
      setLoadingCambiosContador(false);
    }
  };

  const abrirModalCambioContador = async () => {
    const lecturaActual = resumenContador.odometro_actual ?? equipo?.odometro_actual ?? equipo?.odometro ?? 0;
    setActiveTab('mantenimiento');
    setFormCambioContador({
      fecha_cambio: toYmd(),
      lectura_salida: String(lecturaActual),
      lectura_inicial_nueva: '0',
      observaciones: '',
      anclar_mantenimiento: true,
      rutina_inicial: ''
    });
    setShowCambioContador(true);
    await cargarCambiosContador(equipo?.id);
  };

  const registrarCambioContador = async () => {
    if (!equipo?.id) return;
    if (!formCambioContador.fecha_cambio) {
      toast.warning('La fecha de cambio es obligatoria.');
      return;
    }

    const lecturaSalida = Number(formCambioContador.lectura_salida);
    const lecturaInicialNueva = Number(formCambioContador.lectura_inicial_nueva);
    if (Number.isNaN(lecturaSalida) || lecturaSalida < 0) {
      toast.warning('La lectura de salida debe ser un numero mayor o igual a 0.');
      return;
    }
    if (Number.isNaN(lecturaInicialNueva) || lecturaInicialNueva < 0) {
      toast.warning('La lectura inicial del nuevo horometro debe ser mayor o igual a 0.');
      return;
    }

    setSavingCambioContador(true);
    try {
      const payload = {
        fecha_cambio: formCambioContador.fecha_cambio,
        lectura_salida: lecturaSalida,
        lectura_inicial_nueva: lecturaInicialNueva,
        observaciones: formCambioContador.observaciones || '',
      };

      const resp = await apiClient.post(`/equipos/${equipo.id}/registrar-cambio-contador/`, payload);

      if (formCambioContador.anclar_mantenimiento) {
        if (!planAsignado) {
          toast.success('Cambio registrado. No se pudo anclar mantenimiento porque el equipo no tiene plan asignado.');
        } else {
          const payloadInicial = {
            equipo: equipo.id,
            fecha: formCambioContador.fecha_cambio,
            odometro: lecturaInicialNueva,
            reinicio_ciclo: true,
            observaciones: `Ancla por cambio de contador. ${formCambioContador.observaciones || ''}`.trim(),
          };
          if (formCambioContador.rutina_inicial) payloadInicial.rutina = Number(formCambioContador.rutina_inicial);
          await planService.setInicial(payloadInicial);
        }
      }

      setResumenContador({
        odometro_actual: resp?.data?.odometro_actual ?? lecturaInicialNueva,
        total_trabajado: resp?.data?.total_trabajado ?? resumenContador.total_trabajado,
      });
      await cargarCambiosContador(equipo.id);
      setShowCambioContador(false);
      toast.success('Cambio de horómetro registrado correctamente.');
    } catch (err) {
      console.error('Error registrando cambio de contador', err);
      const msg = err?.response?.data?.detail || 'No se pudo registrar el cambio de horómetro.';
      toast.error(msg);
    } finally {
      setSavingCambioContador(false);
    }
  };

  const getUnidadMantenimiento = () => {
    return equipo?.contador_nombre || '';
  };

  const formatNumeroUnidad = (valor, unidad) => {
    if (valor === null || valor === undefined || valor === '') return '-';
    const parsed = Number(valor);
    if (Number.isNaN(parsed)) return String(valor);
    return `${parsed.toLocaleString('es-AR')} ${unidad}`;
  };

  const calcularRestanteMantenimiento = (item = {}) => {
    // odometro_programado null (fila sintética "sin plan") no es un 0 real: sin
    // esta guarda, Number(null) da 0 y el restante sale negativo por el actual
    // solo — un "vencido" fantasma sin ningún programado detrás.
    if (item?.odometro_programado == null) return null;
    const actual = Number(equipo?.odometro_actual ?? equipo?.odometro);
    const programado = Number(item.odometro_programado);
    if (Number.isNaN(actual) || Number.isNaN(programado)) return null;
    return programado - actual;
  };

  const urgenciaMantenimientoLevel = (item = {}) => {
    const backendUrgency = String(item?.urgencia || item?.nivel_urgencia || '').toLowerCase();
    if (backendUrgency) {
      if (['vencido', 'excedido', 'overdue'].includes(backendUrgency)) return 'excedido';
      if (['urgente', 'urgent'].includes(backendUrgency)) return 'urgente';
      if (['proximo', 'próximo', 'near', 'soon'].includes(backendUrgency)) return 'proximo';
      if (['sin_calculo', 'sin cálculo'].includes(backendUrgency)) return 'sin_calculo';
      if (['sin_gestion', 'sin gestión', 'no_gestionado'].includes(backendUrgency)) return 'sin_gestion';
      if (['normal'].includes(backendUrgency)) return 'normal';
    }

    if (item.sin_calculo) return 'sin_calculo';
    if (item.sin_gestion) return 'sin_gestion';
    // Equipo sin plan (o con uno que dejó de corresponder): no hay nada
    // programado contra qué medir "vencido" — manda "reasignar plan", no un
    // vencido calculado contra un odómetro que no significa nada.
    if (item.sin_plan) return 'sin_plan';

    const restanteBackend = item?.horas_restantes;
    const restanteNumerico = Number.isFinite(Number(restanteBackend)) ? Number(restanteBackend) : calcularRestanteMantenimiento(item);
    if (restanteNumerico != null && restanteNumerico <= 0) return 'excedido';

    if (item.esta_vencido) return 'excedido';
    if (item.es_proximo) return 'proximo';
    return 'normal';
  };

  const urgenciaMantenimiento = (item = {}) => {
    const level = urgenciaMantenimientoLevel(item);
    if (level === 'excedido') return 'Vencido';
    if (level === 'urgente') return 'Urgente';
    if (level === 'proximo') return 'Próximo';
    if (level === 'sin_calculo') return 'Sin cálculo';
    if (level === 'sin_gestion') return 'No gestionado';
    if (level === 'sin_plan') return 'Reasignar plan';
    return 'Normal';
  };

  const estadoMantenimientoLabel = (item = {}) => {
    if (item.estado === 'completado') return 'Completado';
    if (item.estado === 'cancelado') return 'Cancelado';
    if (item.esta_vencido) return 'Vencido';
    if (item.es_proximo) return 'Próximo';
    return 'Pendiente';
  };

  const cargarMantenimientosEquipo = useCallback(async () => {
    if (!equipo?.id) {
      setMantenimientoProximos([]);
      setMantenimientoHistorial([]);
      return;
    }

    setMantenimientoLoading(true);
    try {
      const [proximosData, historialData] = await Promise.all([
        mantenimientoService.proximos(),
        mantenimientoService.listarTodos({ equipo: equipo.id })
      ]);

      const proximosList = Array.isArray(proximosData)
        ? proximosData.filter((row) => String(row.equipo) === String(equipo.id))
        : [];
      const historialList = Array.isArray(historialData) ? historialData : [];

      setMantenimientoProximos(proximosList);
      setMantenimientoHistorial(historialList);
    } catch (error) {
      console.error('Error cargando mantenimientos del equipo', error);
      setMantenimientoProximos([]);
      setMantenimientoHistorial([]);
    } finally {
      setMantenimientoLoading(false);
    }
  }, [equipo?.id]);

  useEffect(() => {
    if (activeTab !== 'mantenimiento' || !equipo?.id) return;
    cargarMantenimientosEquipo();
  }, [activeTab, equipo?.id, cargarMantenimientosEquipo]);

  const abrirPlanillaMantenimiento = async (item, mode = 'complete') => {
    try {
      if (!item.id) {
        const creado = await mantenimientoService.programar({
          equipo: item.equipo,
          rutina: item.rutina,
          fecha_programada: item.fecha_programada,
          odometro_programado: item.odometro_programado,
        });
        if (creado?.id) {
          navigate(`/mantenimientos/${creado.id}/planilla?mode=${mode}`);
        }
        return;
      }
      navigate(`/mantenimientos/${item.id}/planilla?mode=${mode}`);
    } catch (error) {
      console.error('Error al abrir planilla de mantenimiento', error);
      toast.error('No se pudo abrir la planilla de mantenimiento.');
    }
  };

  const handleReprogramarMantenimiento = async (item) => {
    if (!item) return;

    const nuevaFecha = await entrada({
      titulo: 'Reprogramar mantenimiento',
      label: 'Nueva fecha (DD-MM-YYYY)',
      tipo: 'text',
      placeholder: 'DD-MM-YYYY',
      valorInicial: item.fecha_programada ? require('../../utils/dateUtils').formatDateDisplay(item.fecha_programada) : '',
      requerido: true,
    });
    if (!nuevaFecha) return;

    const fechaLimpia = String(nuevaFecha || '').trim();
    let fechaProgramada = fechaLimpia;
    if (fechaLimpia) {
      const match = fechaLimpia.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (match) {
        const [, dd, mm, yyyy] = match;
        fechaProgramada = `${yyyy}-${mm}-${dd}`;
      } else if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaLimpia)) {
        toast.warning('Formato de fecha inválido. Usá DD-MM-YYYY.');
        return;
      }
    }

    const odometroActual = item.odometro_programado !== null && item.odometro_programado !== undefined
      ? String(item.odometro_programado)
      : '';
    const nuevoOdometroRaw = await entrada({
      titulo: 'Nuevo teórico programado',
      label: 'Odómetro/horómetro teórico (opcional, dejar vacío para no cambiar)',
      tipo: 'text',
      valorInicial: String(odometroActual || ''),
      placeholder: 'Número o vacío',
    });
    if (nuevoOdometroRaw === null) return;

    const nuevoOdometro = nuevoOdometroRaw.trim() === '' ? null : Number(nuevoOdometroRaw);
    if (nuevoOdometroRaw.trim() !== '' && Number.isNaN(nuevoOdometro)) {
      toast.warning('El teórico programado debe ser numérico.');
      return;
    }

    try {
      if (item.id) {
        await mantenimientoService.actualizarParcial(item.id, {
          fecha_programada: fechaProgramada || null,
          odometro_programado: nuevoOdometro,
        });
      } else {
        await mantenimientoService.reprogramarVisual({
          equipo: item.equipo,
          rutina: item.rutina,
          fecha_programada: fechaProgramada || null,
          odometro_programado: nuevoOdometro,
        });
      }
      await cargarMantenimientosEquipo();
    } catch (error) {
      console.error('Error al reprogramar mantenimiento', error);
      toast.error('No se pudo reprogramar el mantenimiento.');
    }
  };

  const handleEliminarMantenimiento = async (item) => {
    if (!item?.id) return;
    const ok = await confirmar({ titulo: '¿Eliminar mantenimiento del historial?', mensaje: 'Esta acción borrará el registro.', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    try {
      await mantenimientoService.eliminar(item.id);
      await cargarMantenimientosEquipo();
    } catch (error) {
      console.error('Error al eliminar mantenimiento', error);
      toast.error('No se pudo eliminar el mantenimiento.');
    }
  };

  const handleSortMantenimientos = (column, direction) => {
    setMantenimientoSortColumn(column);
    setMantenimientoSortDirection(direction);
    setMantenimientoPage(1);
  };

  const historialMantenimientosOrdenado = React.useMemo(() => {
    const list = [...(mantenimientoHistorial || [])];
    const direction = mantenimientoSortDirection === 'asc' ? 1 : -1;

    const getComparableValue = (item, field) => {
      const value = item?.[field];

      if (field === 'estado') return estadoMantenimientoLabel(item).toLowerCase();
      if (field === 'equipo_nombre') return String(item?.equipo_interno || item?.equipo || '').toLowerCase();
      if (field === 'rutina_nombre' || field === 'tecnico' || field === 'numero_ot') return String(value || '').toLowerCase();
      if (field === 'odometro_programado' || field === 'odometro_realizado') {
        const parsed = Number(value ?? 0);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      if (field === 'fecha_programada' || field === 'fecha_realizada') {
        if (!value) return 0;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
      }

      if (typeof value === 'number') return value;
      return String(value || '').toLowerCase();
    };

    list.sort((a, b) => {
      const va = getComparableValue(a, mantenimientoSortColumn);
      const vb = getComparableValue(b, mantenimientoSortColumn);

      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * direction;
      }
      if (va < vb) return -1 * direction;
      if (va > vb) return 1 * direction;
      return 0;
    });

    return list;
  }, [mantenimientoHistorial, mantenimientoSortColumn, mantenimientoSortDirection]);

  const proximoMantenimiento = React.useMemo(() => {
    const list = [...(mantenimientoProximos || [])];
    list.sort((a, b) => {
      const dateA = a?.fecha_programada ? new Date(a.fecha_programada).getTime() : Number.POSITIVE_INFINITY;
      const dateB = b?.fecha_programada ? new Date(b.fecha_programada).getTime() : Number.POSITIVE_INFINITY;
      if (dateA !== dateB) return dateA - dateB;
      const odoA = Number(a?.odometro_programado ?? Number.MAX_SAFE_INTEGER);
      const odoB = Number(b?.odometro_programado ?? Number.MAX_SAFE_INTEGER);
      return odoA - odoB;
    });
    return list[0] || null;
  }, [mantenimientoProximos]);

  const mantenimientoTotalPages = Math.max(1, Math.ceil((historialMantenimientosOrdenado.length || 0) / mantenimientoPageSize));
  const mantenimientoCurrentPage = Math.min(mantenimientoPage, mantenimientoTotalPages);
  const mantenimientoPaginatedData = React.useMemo(() => {
    const start = (mantenimientoCurrentPage - 1) * mantenimientoPageSize;
    return historialMantenimientosOrdenado.slice(start, start + mantenimientoPageSize);
  }, [historialMantenimientosOrdenado, mantenimientoCurrentPage, mantenimientoPageSize]);

  useEffect(() => {
    if (mantenimientoPage > mantenimientoTotalPages) {
      setMantenimientoPage(mantenimientoTotalPages);
    }
  }, [mantenimientoPage, mantenimientoTotalPages]);

  useEffect(() => {
    setMantenimientoPage(1);
  }, [equipo?.id]);

  const historialColumnsMantenimiento = [
    {
      key: 'numero_ot',
      label: 'N° OT',
      width: '9%',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      cellTitle: (value) => value || '-',
      render: (value) => value || '-',
    },
    {
      key: 'rutina_nombre',
      label: 'Rutina',
      width: '19%',
      sortable: true,
      cellTitle: (value) => value || '-',
      render: (value) => value || '-',
    },
    {
      key: 'fecha_programada',
      label: 'Programado',
      width: '10%',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      cellTitle: (value) => require('../../utils/dateUtils').formatDateDisplay(value),
      render: (value) => require('../../utils/dateUtils').formatDateDisplay(value),
    },
    {
      key: 'fecha_realizada',
      label: 'Realizado',
      width: '10%',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      cellTitle: (value) => value ? require('../../utils/dateUtils').formatDateDisplay(value) : '-',
      render: (value) => value ? require('../../utils/dateUtils').formatDateDisplay(value) : '-',
    },
    {
      key: 'odometro_programado',
      label: 'Teórico',
      width: '11%',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      cellTitle: (value, item) => formatNumeroUnidad(value, getUnidadMantenimiento(item)),
      render: (value, item) => formatNumeroUnidad(value, getUnidadMantenimiento(item)),
    },
    {
      key: 'odometro_realizado',
      label: 'Real',
      width: '11%',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      cellTitle: (value, item) => formatNumeroUnidad(value, getUnidadMantenimiento(item)),
      render: (value, item) => formatNumeroUnidad(value, getUnidadMantenimiento(item)),
    },
    {
      key: 'estado',
      label: 'Estado',
      width: '9%',
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      cellTitle: (_value, item) => estadoMantenimientoLabel(item),
      render: (_value, item) => estadoMantenimientoLabel(item),
    },
    {
      key: 'tecnico',
      label: 'Técnico',
      width: '9%',
      sortable: true,
      // En un servicio hecho en terceros no hay técnico propio: se muestra el proveedor.
      cellTitle: (value, item) => {
        if (!item?.realizado_en_terceros) return value || '-';
        const costo = Number(item.gastos_extra_mantenimiento);
        return [
          `Realizado en terceros: ${item.proveedor_nombre || '-'}`,
          costo > 0 ? `Costo: $ ${costo.toLocaleString('es-AR')}` : null,
          item.numero_comprobante ? `Comprobante: ${item.numero_comprobante}` : null,
        ].filter(Boolean).join(' · ');
      },
      render: (value, item) => (item?.realizado_en_terceros ? (item.proveedor_nombre || 'Terceros') : (value || '-')),
    },
    {
      key: 'observaciones_realizacion',
      label: 'Motivo / Obs.',
      width: '12%',
      cellTitle: (_value, item) => item.observaciones_realizacion || item.observaciones || '-',
      render: (_value, item) => item.observaciones_realizacion || item.observaciones || '-',
    },
    {
      key: 'acciones',
      label: 'Acciones',
      width: '10%',
      align: 'center',
      headerAlign: 'center',
      render: (_value, item) => (
        <HoverRowActions>
          <ActionButton title="Planilla" onClick={() => abrirPlanillaMantenimiento(item, 'readonly')}>
            <i className="bi bi-file-earmark-text" />
          </ActionButton>
          {item.estado !== 'completado' && item.estado !== 'cancelado' && (
            <>
              <ActionButton $variant="warning" title="Reprogramar" onClick={() => handleReprogramarMantenimiento(item)}>
                <i className="bi bi-pencil" />
              </ActionButton>
            </>
          )}
          {item.id && (
            <ActionButton $variant="delete" title="Eliminar" onClick={() => handleEliminarMantenimiento(item)}>
              <i className="bi bi-trash" />
            </ActionButton>
          )}
        </HoverRowActions>
      ),
    }
  ];

  const tabs = [
    { id: 'info', icon: 'bi bi-card-text', label: 'Detalles' },
    { id: 'mantenimiento', icon: 'bi bi-wrench', label: 'Mantenimientos' },
    { id: 'repuestos', icon: 'bi bi-box-seam', label: 'Repuestos y capacidades' },
    { id: 'partes', icon: 'bi bi-journal-text', label: 'Partes' },
    { id: 'reparaciones', icon: 'bi bi-tools', label: 'OT Correctivas' },
    { id: 'lubricantes', icon: 'bi bi-droplet-half', label: 'Lubricantes' },
    { id: 'documentacion', icon: 'bi bi-file-earmark-text', label: 'Documentación' },
    { id: 'checklist', icon: 'bi bi-clipboard-check', label: 'Check Lists' },
    { id: 'traslados', icon: 'bi bi-truck', label: 'Traslados' },
  ];

  // Column configuration for Partes Diarios (to keep consistency with ListaPartesDiarios page)
  const columnsPartes = [
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      sortKey: 'equipo__interno',
      width: '12%',
      render: (value, row) => (
        <LinkEquipo
          href={`/?equipoId=${row.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${row.equipo}`;
          }}
        >
          {value}
        </LinkEquipo>
      )
    },
    { key: 'fecha', label: 'Fecha', sortable: true, sortKey: 'fecha', width: '10%', render: (v) => v ? new Date(v + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-' },
    { key: 'operador', label: 'Operador', sortable: true, sortKey: 'operador', width: '13%' },
    { key: 'centro_costo_nombre', label: 'Centro de Costo', sortable: true, sortKey: 'centro_costo__nombre', width: '12%' },
    { key: 'obra_nombre', label: 'Obra', sortable: true, sortKey: 'obra__nombre', width: '10%', render: (v) => v || '-' },
    { key: 'sector_nombre', label: 'Sector', sortable: true, sortKey: 'sector__nombre', width: '9%', render: (v) => v || '-' },
    { key: 'kilometros', label: 'Kilómetros', sortable: true, sortKey: 'kilometros', width: '7%', render: (v) => v ? `${v} km` : '-' },
    { key: 'horas', label: 'Horas', sortable: true, sortKey: 'horas', width: '7%', render: (v) => v ? `${v} hs` : '-' },
    {
      key: 'tareas',
      label: 'Tareas',
      sortable: false,
      width: '12%',
      cellTitle: (v) => (v ? String(v) : '-'),
      render: (v) => {
        if (!v) return '-';
        return String(v).length > 30 ? `${String(v).substring(0, 30)}...` : String(v);
      }
    },
    {
      key: 'observaciones',
      label: 'Observaciones',
      sortable: false,
      width: '12%',
      cellTitle: (v) => (v ? String(v) : '-'),
      render: (v) => (v || '-')
    }
  ];

  // Column configuration for OTC (órdenes correctivas)
  const columnsReparaciones = [
    { key: 'numero_ot', label: 'Nro. OTC', sortable: true, sortKey: 'numero_ot', width: '12%' },
    { key: 'equipo_interno', label: 'Equipo', sortable: true, sortKey: 'equipo__interno', width: '13%', render: (value, item) => (
      <LinkEquipo href={`/?equipoId=${item.equipo}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/?equipoId=${item.equipo}`; }}>{value}</LinkEquipo>
    ) },
    { key: 'titulo', label: 'Título', sortable: true, sortKey: 'titulo', width: '20%', cellTitle: (v) => (v ? String(v) : '-') },
    { key: 'estado', label: 'Estado', sortable: true, sortKey: 'estado', width: '10%', render: (v) => {
      if (v === 'abierta') return 'Abierta';
      if (v === 'cerrada') return 'Cerrada';
      if (v === 'cancelada') return 'Cancelada';
      return v || '-';
    } },
    { key: 'mecanico_responsable_nombre', label: 'Mecánico', sortable: true, sortKey: 'mecanico_responsable__nombre', width: '13%', render: (v) => v || '-' },
    { key: 'fecha_apertura', label: 'Apertura', sortable: true, sortKey: 'fecha_apertura', width: '11%', render: (v) => require('../../utils/dateUtils').formatDateDisplay(v) },
    { key: 'fecha_cierre', label: 'Cierre', sortable: true, sortKey: 'fecha_cierre', width: '11%', render: (v) => v ? require('../../utils/dateUtils').formatDateDisplay(v) : '-' },
    {
      key: 'descripcion',
      label: 'Descripción',
      sortable: false,
      width: '18%',
      cellTitle: (v) => (v ? String(v) : '-'),
      render: (v) => (v || '-')
    }
  ];

  // Column configuration for unified Lubricantes register (aceites + engrases)
  const columnsLubricantes = [
    { key: 'equipo_interno', label: 'Equipo', sortable: true, sortKey: 'equipo__interno', width: '12%', render: (value, item) => (
      <LinkEquipo href={`/?equipoId=${item.equipo}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/?equipoId=${item.equipo}`; }}>{value}</LinkEquipo>
    ) },
    { key: 'fecha', label: 'Fecha', sortable: true, sortKey: 'fecha', width: '10%', render: (v) => require('../../utils/dateUtils').formatDateDisplay(v) },
    { key: 'lubricante_nombre', label: 'Lubricante', sortable: true, sortKey: 'lubricante__nombre', width: '15%', render: (v) => v || '-' },
    { key: 'cantidad', label: 'Cantidad', sortable: true, sortKey: 'cantidad', width: '10%', render: (v, item) => v ? `${v} ${item.lubricante_unidad_medida || ''}`.trim() : '-' },
    { key: 'centro_costo_nombre', label: 'Centro de Costo', sortable: true, sortKey: 'centro_costo__nombre', width: '12%', render: (v) => v || '-' },
    { key: 'tarea_nombre', label: 'Tarea', sortable: true, sortKey: 'tarea__nombre', width: '15%', render: (v) => v || '-' },
    { key: 'mecanico_nombre', label: 'Mecánico', sortable: true, sortKey: 'mecanico__nombre', width: '12%', render: (v) => v || '-' },
    { key: 'chofer', label: 'Chofer', sortable: true, sortKey: 'chofer', width: '10%', render: (v) => v || '-' },
    {
      key: 'observaciones',
      label: 'Observaciones',
      sortable: false,
      width: '14%',
      cellTitle: (v) => (v ? String(v) : '-'),
      render: (v) => (v || '-')
    }
  ];

  // Column configuration for Documentación
  const columnsDocumentacion = [
    { key: 'equipo_interno', label: 'Equipo', sortable: true, sortKey: 'equipo__interno', width: '15%', render: (value, item) => (
      <LinkEquipo href={`/?equipoId=${item.equipo}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/?equipoId=${item.equipo}`; }}>{value}</LinkEquipo>
    ) },
  { key: 'tipo_nombre', label: 'Tipo Documento', sortable: true, sortKey: 'tipo_nombre', width: '17%' },
  { key: 'fecha_emision', label: 'Emisión', sortable: true, sortKey: 'fecha_emision', width: '11%', render: (value) => { if (!value) return '-'; const { formatDateDisplay } = require('../../utils/dateUtils'); return formatDateDisplay(value); } },
  { key: 'fecha_vencimiento', label: 'Vencimiento', sortable: true, sortKey: 'fecha_vencimiento', width: '11%', render: (value, item) => { if (item.no_vence) return <span style={{ color: theme.colors.textSecondary }}>No vence</span>; if (!value) return '-'; const { formatDateDisplay, parseDateLocal, toLocalYMD } = require('../../utils/dateUtils'); const isExpired = parseDateLocal(value) < parseDateLocal(toLocalYMD(new Date())); return (<span style={{ color: isExpired ? theme.colors.danger : theme.colors.inherit }}>{formatDateDisplay(value)}</span>); } },
    { key: 'archivo', label: 'Archivo', sortable: false, width: '12%', render: (value, item) => {
      const adjuntos = item.adjuntos || [];
      if (adjuntos.length === 0) return '-';
      return (
        <LinkAdjuntos onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDocumentoAdjuntos(item); }}>
          <i className="fas fa-file-pdf"></i> Ver{adjuntos.length > 1 ? ` (${adjuntos.length})` : ''}
        </LinkAdjuntos>
      );
    } },
    {
      key: 'notas',
      label: 'Notas',
      sortable: false,
      width: '24%',
      cellTitle: (v) => (v ? String(v) : '-'),
      render: (v) => (v || '-')
    }
  ];

  // Column configuration for repuestos recomendados (pestaña Repuestos y capacidades)
  const columnsRepuestosRecomendados = [
    { key: 'repuesto_codigo', label: 'Código', sortable: true, sortKey: 'repuesto__codigo', width: '14%', render: (v, rr) => v || (rr.repuesto && rr.repuesto.codigo) || '-' },
    { key: 'repuesto_marca', label: 'Marca', sortable: true, sortKey: 'repuesto__marca__nombre', width: '14%', render: (v, rr) => v || (rr.repuesto && rr.repuesto.marca_nombre) || '-' },
    { key: 'repuesto_descripcion', label: 'Descripción', sortable: true, sortKey: 'repuesto__descripcion', width: '24%', render: (v, rr) => v || (rr.repuesto && rr.repuesto.descripcion) || '-' },
    { key: 'componente_nombre', label: 'Componente', sortable: true, sortKey: 'componente__nombre', width: '20%', render: (v, rr) => v || (rr.componente && rr.componente.nombre) || 'Sin componente' },
    { key: 'cantidad', label: 'Cantidad', sortable: true, sortKey: 'cantidad', width: '10%' },
    { key: 'repuesto_unidad_medida', label: 'Unidad', sortable: false, width: '10%', render: (v) => v || '-' },
  ];

  // Column configuration for capacidades de llenado (pestaña Repuestos y capacidades)
  const columnsCapacidadesLlenado = [
    {
      key: 'fluido_nombre', label: 'Fluido / Combustible', sortable: true, sortKey: 'fluido__nombre', width: '38%',
      render: (v, cap) => (
        <>
          {v || '-'}
          {cap.es_combustible && (
            <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: theme.colors.bgSecundario, color: theme.colors.txtSecondary, border: `1px solid ${theme.colors.borderDefault}`, whiteSpace: 'nowrap' }}>
            Combustible
            </span>
          )}
        </>
      ),
    },
    { key: 'deposito_nombre', label: 'Depósito', sortable: true, sortKey: 'deposito__nombre', width: '32%', render: (v) => v || 'Sin depósito' },
    { key: 'capacidad_litros', label: 'Capacidad', sortable: true, sortKey: 'capacidad_litros', width: '15%' },
    { key: 'fluido_unidad_medida', label: 'Unidad', sortable: false, width: '15%', render: (v) => v || 'L' },
  ];

  if (!equipo) {
    return (
      <DetalleContainer>
        <EmptyState>
          <i className="bi bi-gear"></i>
          <h4>Selecciona un equipo</h4>
          <p>Haz clic en un equipo del árbol para ver sus detalles</p>
        </EmptyState>
      </DetalleContainer>
    );
  }

  return (
    <DetalleContainer>
      {/* HEADER CON PESTAÑAS Y BOTONES */}
      <TabHeader>
        <TabsList>
          {tabs.map(tab => (
            <TabItem key={tab.id}>
              <TabLink 
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </TabLink>
            </TabItem>
          ))}
        </TabsList>
        
        <HeaderButtons>
          <HeaderButton 
            title="Editar equipo"
            onClick={() => navigate(`/equipos/${equipo.id}/editar`)}
          >
            <i className="bi bi-pencil"></i>
            Editar
          </HeaderButton>
          <HeaderButton
            title="Clonar equipo"
            onClick={() => navigate(`/equipos/${equipo.id}/clonar`)}
          >
            <i className="bi bi-copy"></i>
            Clonar
          </HeaderButton>
          <HeaderButton
            className="btn-primary" 
            title="Agregar equipo"
            onClick={() => navigate('/equipos/crear')}
          >
            <i className="bi bi-plus"></i>
            Agregar nuevo
          </HeaderButton>
        </HeaderButtons>
      </TabHeader>

      {/* CONTENIDO DE LAS PESTAÑAS */}
      <TabContent>
        {/* TAB DETALLES */}
        <TabPane $active={activeTab === 'info'}>
          <TabTitle><i className="bi bi-card-text" style={{ marginLeft: 8, color: theme.colors.primary }} /> Detalle del Equipo</TabTitle>
          
          <CardsGrid>
            {/* CARD: IDENTIFICACIÓN */}
            <Card>
              <CardHeader>Identificación</CardHeader>
              <CardBody>
                <CardRow>
                  <CardLabel>Interno:</CardLabel>
                  <CardValue>{equipo.interno || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Descripción:</CardLabel>
                  <CardValue>{equipo.descripcion || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Patente:</CardLabel>
                  <CardValue>{equipo.patente || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Chasis:</CardLabel>
                  <CardValue>{equipo.chasis || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Motor:</CardLabel>
                  <CardValue>{equipo.motor || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Año:</CardLabel>
                  <CardValue>{equipo.anio || '-'}</CardValue>
                </CardRow>
              </CardBody>
            </Card>

            {/* CARD: CARACTERÍSTICAS */}
            <Card>
              <CardHeader>Características</CardHeader>
              <CardBody>
                <CardRow>
                  <CardLabel>Marca:</CardLabel>
                  <CardValue>{equipo.marca_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Modelo:</CardLabel>
                  <CardValue>{equipo.modelo_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Potencia:</CardLabel>
                  <CardValue>{equipo.potencia ? `${equipo.potencia} HP` : '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Capacidad:</CardLabel>
                  <CardValue>{equipo.capacidad || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Familia:</CardLabel>
                  <CardValue>{equipo.familia_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Tipo:</CardLabel>
                  <CardValue>{equipo.tipo_nombre || '-'}</CardValue>
                </CardRow>
              </CardBody>
            </Card>

            {/* CARD: UBICACIÓN */}
            <Card>
              <CardHeader>Ubicación</CardHeader>
              <CardBody>
                <CardRow>
                  <CardLabel>Sucursal:</CardLabel>
                  <CardValue>{equipo.sucursal_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Empresa:</CardLabel>
                  <CardValue>{equipo.empresa_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Obra:</CardLabel>
                  <CardValue>{equipo.obra_nombre || '-'}</CardValue>
                </CardRow>
                 <CardRow>
                  <CardLabel>Ubicación:</CardLabel>
                  <CardValue>{equipo.ubicacion_nombre || '-'}</CardValue>
                </CardRow>               
                <CardRow>
                  <CardLabel>Centro de costo:</CardLabel>
                  <CardValue>{equipo.centro_costo_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Ubicación GPS:</CardLabel>
                  <CardValue>
                    <button
                      onClick={() => setShowMapaGPS(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: theme.colors.info,
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        fontWeight: 500,
                      }}
                    >
                      <i className="fas fa-map-marker-alt" />
                      Ver en mapa
                    </button>
                  </CardValue>
                </CardRow>
              </CardBody>
            </Card>

            {/* CARD: ESTADO */}
            <Card>
              <CardHeader>Estado de uso</CardHeader>
              <CardBody>
                <CardRow>
                  <CardLabel>Estado:</CardLabel>
                  <CardValue>{equipo.estado_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Horómetro actual:</CardLabel>
                  <CardValue>{(resumenContador.odometro_actual ?? equipo.odometro_actual ?? equipo.odometro ?? 0).toLocaleString('es-AR')}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Total trabajado:</CardLabel>
                  <CardValue>{(resumenContador.total_trabajado ?? equipo.total_trabajado ?? equipo.odometro_actual ?? equipo.odometro ?? 0).toLocaleString('es-AR')}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Contador:</CardLabel>
                  <CardValue>{equipo.contador_nombre || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Fecha de lectura:</CardLabel>
                  <CardValue>
                    {equipo.odometro_fecha 
                      ? require('../../utils/dateUtils').formatDateDisplay(equipo.odometro_fecha)
                      : '-'
                    }
                  </CardValue>
                </CardRow>
                <div style={{ gridColumn: '1 / -1', marginTop: 10 }}>
                  <button
                    style={{
                      width: '100%',
                      background: theme.colors.primary,
                      color: theme.colors.onPrimary,
                      border: `1px solid ${theme.colors.primary}`,
                      borderRadius: 6,
                      padding: '8px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                    onClick={abrirModalCambioContador}
                    title="Registrar cambio de horómetro"
                  >
                    <i className="bi bi-arrow-repeat" style={{ fontSize: 13 }} /> Registrar cambio de horómetro
                  </button>
                </div>
              </CardBody>
            </Card>
            {/* CARD: VINCULACIÓN (implemento montado / implementos montados sobre este equipo) */}
            {(vinculacion.montaje_vigente || vinculacion.implementos_montados_vigentes.length > 0) && (
              <Card>
                <CardHeader>Vinculación</CardHeader>
                <CardBody>
                  {vinculacion.montaje_vigente ? (
                    <>
                      <CardRow>
                        <CardLabel>Montado sobre:</CardLabel>
                        <CardValue>
                          <button
                            type="button"
                            onClick={() => navigate(`/?equipoId=${vinculacion.montaje_vigente.portador_id}`)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: theme.colors.info, fontWeight: 500 }}
                          >
                            {vinculacion.montaje_vigente.portador_interno}
                          </button>
                        </CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Desde:</CardLabel>
                        <CardValue>{require('../../utils/dateUtils').formatDateDisplay(vinculacion.montaje_vigente.fecha_montaje)}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>Hereda horas:</CardLabel>
                        <CardValue>{vinculacion.montaje_vigente.hereda_horas ? 'Sí' : 'No'}</CardValue>
                      </CardRow>
                      {vinculacion.montaje_vigente.hereda_horas && (
                        <CardRow>
                          <CardLabel>Horas heredadas acumuladas:</CardLabel>
                          <CardValue>{horasHeredadas != null ? horasHeredadas.toLocaleString('es-AR') : '...'}</CardValue>
                        </CardRow>
                      )}
                    </>
                  ) : null}
                  {vinculacion.implementos_montados_vigentes.length > 0 && (
                    <>
                      {vinculacion.montaje_vigente && <div style={{ gridColumn: '1 / -1', marginTop: 8, marginBottom: 4, fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary }}>IMPLEMENTOS MONTADOS</div>}
                      {vinculacion.implementos_montados_vigentes.map((im, idx) => (
                        <React.Fragment key={im.id}>
                          {idx > 0 && (
                            <div style={{ gridColumn: '1 / -1', borderTop: `1px solid ${theme.colors.borderLight}`, margin: '6px 0' }} />
                          )}
                          <CardRow>
                            <CardLabel>Montado con:</CardLabel>
                            <CardValue>
                              <button
                                type="button"
                                onClick={() => navigate(`/?equipoId=${im.implemento_id}`)}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: theme.colors.info, fontWeight: 500 }}
                              >
                                {im.implemento_interno}
                              </button>
                            </CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>Desde:</CardLabel>
                            <CardValue>{require('../../utils/dateUtils').formatDateDisplay(im.fecha_montaje)}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>Hereda horas:</CardLabel>
                            <CardValue>{im.hereda_horas ? 'Sí' : 'No'}</CardValue>
                          </CardRow>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </CardBody>
              </Card>
            )}
            {/* CARD: DATOS DE TRANSPORTE */}
            <Card>
              <CardHeader>Datos de Transporte</CardHeader>
              <CardBody>
                <CardRow>
                  <CardLabel>Largo:</CardLabel>
                  <CardValue>{equipo.largo_transporte != null ? `${equipo.largo_transporte} m` : '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Ancho:</CardLabel>
                  <CardValue>{equipo.ancho_transporte != null ? `${equipo.ancho_transporte} m` : '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Alto:</CardLabel>
                  <CardValue>{equipo.alto_transporte != null ? `${equipo.alto_transporte} m` : '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Peso:</CardLabel>
                  <CardValue>{equipo.peso_transporte != null ? `${equipo.peso_transporte} kg` : '-'}</CardValue>
                </CardRow>
              </CardBody>
            </Card>
          </CardsGrid>

          {/* (Card de configuración movido a pestaña Mantenimiento) */}
        </TabPane>

        {/* TAB MANTENIMIENTOS */}
        <TabPane $active={activeTab === 'mantenimiento'}>
          <SectionHeader style={{ marginBottom: 10, flexWrap: 'wrap' }}>
            <SectionTitle style={{ fontSize: '1rem' }}><i className="bi bi-wrench" style={{ color: theme.colors.primary }} /> Estado de Mantenimientos</SectionTitle>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              {planAsignado && (
                <>
                  <a
                    href={`/planes-mantenimiento/${planAsignado.plan}/grilla?equipo=${equipo?.id}&from=equipo&equipoId=${equipo?.id}`}
                    title="Ver grilla del plan de mantenimiento"
                    style={{ textDecoration:'none' }}
                    onClick={(e)=>{ e.preventDefault(); window.location.assign(`/planes-mantenimiento/${planAsignado.plan}/grilla?equipo=${equipo?.id}&from=equipo&equipoId=${equipo?.id}`); }}
                  >
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, background: theme.colors.surface, border:`1px solid ${theme.colors.borderDefault}`, padding:'2px 10px', borderRadius:12, cursor:'pointer' }}>
                      <i className="bi bi-grid-3x3-gap" style={{ fontSize:13, color: theme.colors.info }} /> {planAsignado.plan_nombre || 'Plan de mantenimiento'}
                    </span>
                  </a>
                  {planAsignado.origen === 'manual' && (
                    <span title="Elegido a mano: los cambios de marca o modelo no lo modifican" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, background: theme.colors.bgSecundario, border:`1px solid ${theme.colors.borderDefault}`, padding:'2px 10px', borderRadius:12 }}>
                      <i className="bi bi-hand-index" style={{ fontSize:13, color: theme.colors.info }} /> Asignado a mano
                    </span>
                  )}
                  {!planAsignado.requiere_revision && (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, background: theme.colors.bgSecundario, border:`1px solid ${theme.colors.borderDefault}`, padding:'2px 10px', borderRadius:12 }}>
                      <i className="bi bi-list-check" style={{ fontSize:13, color: theme.colors.success }} /> {rutinas.length} rutina{rutinas.length === 1 ? '' : 's'}
                    </span>
                  )}
                </>
              )}
              {/* Sin plan, o con uno que ya no corresponde (cambió marca/modelo/contador): un
                  badge pasivo ("Sin plan" / "A revisar") describe el problema pero no dice qué
                  hacer. Esto ES la acción, en ámbar para que salte a la vista — abre el mismo
                  modal que "Gestionar plan". */}
              {(!planAsignado || planAsignado.requiere_revision) ? (
                <button
                  style={{ background: theme.colors.warningBg, color: theme.colors.warningTxt, border:`1px solid ${theme.colors.warningBorder}`, borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}
                  onClick={()=> setShowPlanEquipo(true)}
                  title={planAsignado ? (planAsignado.revision_motivo || 'El plan asignado ya no corresponde por marca, modelo o contador.') : 'Sin plan no se calculan próximos servicios ni se avisan vencimientos.'}
                >
                  <i className="bi bi-exclamation-triangle-fill" style={{ fontSize:13 }} /> Asignar plan manualmente
                </button>
              ) : (
                <button
                  style={{ background: theme.colors.transparent, color: theme.colors.primary, border:`1px solid ${theme.colors.primary}`, borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}
                  onClick={()=> setShowPlanEquipo(true)}
                  title="Ver el plan de mantenimiento de este equipo"
                >
                  <i className="bi bi-clipboard-check" style={{ fontSize:14 }} /> Gestionar plan
                </button>
              )}
              <button
                style={{ background: theme.colors.transparent, color: theme.colors.primary, border:`1px solid ${theme.colors.primary}`, borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}
                onClick={()=> setShowConfig(true)}
                title="Configurar plan / último mantenimiento"
              >
                <i className="bi bi-sliders" style={{ fontSize:14 }} /> Configurar mantenimiento inicial
              </button>
            </div>
          </SectionHeader>
          {equipo?.id && (
            <div style={{ display:'flex', flexDirection:'column', gap:10, minHeight:0, flex:1 }}>
              <div style={{ background: theme.colors.surface, border:`1px solid ${theme.colors.borderDefault}`, borderRadius:10, padding:'10px 12px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:8 }}>
                  <strong style={{ display:'inline-flex', alignItems:'center', gap:6, whiteSpace: 'nowrap', fontSize: '0.83rem', fontWeight: 700 }}>
                    <i className="bi bi-calendar2-check" style={{ color: theme.colors.info }} />
                    Mantenimiento programado del equipo
                  </strong>
                  <button
                    type="button"
                    onClick={cargarMantenimientosEquipo}
                    style={{
                      border:`1px solid ${theme.colors.borderDefault}`,
                      background: theme.colors.transparent,
                      color: theme.colors.txtSecondary,
                      borderRadius: 6,
                      padding: '4px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <i className="bi bi-arrow-clockwise" /> Actualizar
                  </button>
                </div>

                {proximoMantenimiento ? (
                  <ProgrammedSummaryRow>
                    <ProgrammedMetric><ProgrammedMetricLabel>N° OT:</ProgrammedMetricLabel><span>{proximoMantenimiento.numero_ot || '-'}</span></ProgrammedMetric>
                    <ProgrammedMetric><ProgrammedMetricLabel>Rutina:</ProgrammedMetricLabel><span>{proximoMantenimiento.rutina_nombre || '-'}</span></ProgrammedMetric>
                    <ProgrammedMetric><ProgrammedMetricLabel>Programado:</ProgrammedMetricLabel><span>{require('../../utils/dateUtils').formatDateDisplay(proximoMantenimiento.fecha_programada)}</span></ProgrammedMetric>
                    <ProgrammedMetric><ProgrammedMetricLabel>Teórico:</ProgrammedMetricLabel><span>{formatNumeroUnidad(proximoMantenimiento.odometro_programado, getUnidadMantenimiento(proximoMantenimiento))}</span></ProgrammedMetric>
                    <ProgrammedMetric><ProgrammedMetricLabel>Horómetro actual:</ProgrammedMetricLabel><span>{formatNumeroUnidad(equipo?.odometro_actual ?? equipo?.odometro, getUnidadMantenimiento(proximoMantenimiento))}</span></ProgrammedMetric>
                    <ProgrammedMetric><ProgrammedMetricLabel>Restante:</ProgrammedMetricLabel><span>{(() => {
                      const restante = calcularRestanteMantenimiento(proximoMantenimiento);
                      if (restante === null) return '-';
                      const unidad = getUnidadMantenimiento(proximoMantenimiento);
                      return formatNumeroUnidad(restante, unidad);
                    })()}</span></ProgrammedMetric>
                    <ProgrammedMetric>
                      <ProgrammedMetricLabel>Urgencia:</ProgrammedMetricLabel>
                      <UrgenciaBadge $tone={urgenciaMantenimientoLevel(proximoMantenimiento)}>{urgenciaMantenimiento(proximoMantenimiento)}</UrgenciaBadge>
                    </ProgrammedMetric>
                    {!proximoMantenimiento.sin_plan && (
                      <ProgrammedActions>
                        <ActionButton title="Reprogramar" $variant="warning" onClick={() => handleReprogramarMantenimiento(proximoMantenimiento)}>
                          <i className="bi bi-pencil" />
                        </ActionButton>
                        <ActionButton title="Planilla" onClick={() => abrirPlanillaMantenimiento(proximoMantenimiento, 'complete')}>
                          <i className="bi bi-file-earmark-text" />
                        </ActionButton>
                      </ProgrammedActions>
                    )}
                  </ProgrammedSummaryRow>
                ) : (
                  <div style={{ color: theme.colors.txtSecondary, fontSize: 13 }}>
                    No hay mantenimientos próximos para este equipo.
                  </div>
                )}
              </div>

              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <SectionTitle>
                    <i className="bi bi-clock-history" style={{ color: theme.colors.primary }} />
                    Historial de mantenimientos del equipo
                  </SectionTitle>
                </SectionHeader>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Table
                    columns={historialColumnsMantenimiento}
                    data={mantenimientoPaginatedData}
                    loading={mantenimientoLoading}
                    emptyMessage="No hay historial de mantenimiento para este equipo"
                    onSort={handleSortMantenimientos}
                    sortColumn={mantenimientoSortColumn}
                    sortDirection={mantenimientoSortDirection}
                    showActions={false}
                    maxHeight="100%"
                    noExtraBottomSpace
                    horizontalScroll
                    tableVisualVariant="rows"
                  />
                </div>
                <Pagination
                  currentPage={mantenimientoCurrentPage}
                  totalPages={mantenimientoTotalPages}
                  totalItems={historialMantenimientosOrdenado.length}
                  itemsPerPage={mantenimientoPageSize}
                  perPageOptions={[10, 25, 50]}
                  onPageChange={(page) => setMantenimientoPage(page)}
                  onPerPageChange={(size) => {
                    setMantenimientoPageSize(size);
                    setMantenimientoPage(1);
                  }}
                  showPerPageSelector
                  showTotalCounter
                />
              </div>
            </div>
          )}
          {/* Modal de configuración de mantenimiento inicial */}
          <ModalPlanEquipo
            open={showPlanEquipo}
            onClose={() => setShowPlanEquipo(false)}
            equipoId={equipo?.id}
            equipoLabel={equipo ? `${equipo.interno || equipo.codigo_interno || equipo.id} · ${equipo.descripcion || ''}`.trim() : ''}
            onSuccess={async () => {
              try { await recargarPlanAsignado(); } catch {}
              try { await cargarMantenimientosEquipo(); } catch {}
            }}
          />
          <ModalMantenimientoInicial
            open={showConfig}
            onClose={() => setShowConfig(false)}
            equipoId={equipo?.id}
            equipoLabel={equipo ? `${equipo.interno || equipo.codigo_interno || equipo.id} · ${equipo.descripcion || ''}`.trim() : ''}
            unidad={equipo?.contador_nombre || ''}
            onSuccess={async () => {
              try { await cargarMantenimientosEquipo(); } catch {}
            }}
          />

          {showCambioContador && (
            <div
              style={{ position:'fixed', inset:0, background: theme.colors.overlayBackdropSoft, display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
              onMouseDown={cambioBackdropGuard.onBackdropMouseDown}
              onClick={cambioBackdropGuard.buildOnBackdropClick(() => setShowCambioContador(false))}
            >
              <div style={{ background: theme.colors.surface, color: theme.colors.txtPrincipal, border:`1px solid ${theme.colors.borderDefault}`, borderRadius:12, width:'min(820px, 94%)', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow: theme.shadows.lg }} onClick={(e)=>e.stopPropagation()}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid ${theme.colors.borderDefault}` }}>
                  <strong style={{ display:'flex', alignItems:'center', gap:6 }}><i className="bi bi-arrow-repeat"/> Cambio de horometro</strong>
                  <button onClick={()=>setShowCambioContador(false)} style={{ background: theme.colors.transparent, border:`1px solid ${theme.colors.borderDefault}`, borderRadius:6, padding:'6px 10px', cursor:'pointer' }} title="Cerrar">
                    <i className="bi bi-x" />
                  </button>
                </div>
                <div style={{ padding:'14px 16px', overflowY:'auto', display:'flex', flexDirection:'column', gap:14 }}>
                  <div style={{ background: theme.colors.bgSecundario, border:`1px solid ${theme.colors.borderDefault}`, borderRadius:8, padding:10, fontSize:12 }}>
                    Se creara un parte automatico de corte con la lectura inicial que confirmes para el contador nuevo.
                    Eso mantiene validaciones de partes sin romper la secuencia operativa.
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:10 }}>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      <label style={{ fontSize:12, fontWeight:600 }}>Fecha de cambio</label>
                      <input
                        type="date"
                        value={formCambioContador.fecha_cambio}
                        onChange={(e)=>setFormCambioContador(v=>({ ...v, fecha_cambio: e.target.value }))}
                        style={{ width:'100%', height:34, borderRadius:6, background: theme.colors.bgSecundario, color: theme.colors.txtPrincipal, border:`1px solid ${theme.colors.borderDefault}`, padding:'0 8px', fontSize:12 }}
                      />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      <label style={{ fontSize:12, fontWeight:600 }}>Lectura al retirar (saliente)</label>
                      <input
                        type="number"
                        min="0"
                        value={formCambioContador.lectura_salida}
                        onChange={(e)=>setFormCambioContador(v=>({ ...v, lectura_salida: e.target.value }))}
                        style={{ width:'100%', height:34, borderRadius:6, background: theme.colors.bgSecundario, color: theme.colors.txtPrincipal, border:`1px solid ${theme.colors.borderDefault}`, padding:'0 8px', fontSize:12 }}
                      />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      <label style={{ fontSize:12, fontWeight:600 }}>Lectura inicial del nuevo</label>
                      <input
                        type="number"
                        min="0"
                        value={formCambioContador.lectura_inicial_nueva}
                        onChange={(e)=>setFormCambioContador(v=>({ ...v, lectura_inicial_nueva: e.target.value }))}
                        style={{ width:'100%', height:34, borderRadius:6, background: theme.colors.bgSecundario, color: theme.colors.txtPrincipal, border:`1px solid ${theme.colors.borderDefault}`, padding:'0 8px', fontSize:12 }}
                      />
                    </div>
                  </div>

                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    <label style={{ fontSize:12, fontWeight:600 }}>Observaciones</label>
                    <textarea
                      value={formCambioContador.observaciones}
                      onChange={(e)=>setFormCambioContador(v=>({ ...v, observaciones: e.target.value }))}
                      style={{ width:'100%', minHeight:70, borderRadius:6, background: theme.colors.bgSecundario, color: theme.colors.txtPrincipal, border:`1px solid ${theme.colors.borderDefault}`, padding:'8px', fontSize:12 }}
                    />
                  </div>

                  <div style={{ border:`1px solid ${theme.colors.borderDefault}`, borderRadius:8, padding:10, display:'flex', flexDirection:'column', gap:8 }}>
                    <label style={{ display:'inline-flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600 }}>
                      <input
                        type="checkbox"
                        checked={formCambioContador.anclar_mantenimiento}
                        onChange={(e)=>setFormCambioContador(v=>({ ...v, anclar_mantenimiento: e.target.checked }))}
                      />
                      Anclar proximo mantenimiento desde la lectura inicial del contador nuevo
                    </label>
                    {formCambioContador.anclar_mantenimiento && (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:10 }}>
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                          <label style={{ fontSize:12, fontWeight:600 }}>Rutina inicial (opcional)</label>
                          <SearchableSelectField
                            options={rutinas}
                            value={formCambioContador.rutina_inicial}
                            onChange={(value)=>setFormCambioContador(v=>({ ...v, rutina_inicial: value }))}
                            placeholder="Auto (recomendado)"
                            getOptionValue={(option) => String(option.id)}
                            getOptionLabel={(option) => `${option.nombre || ''}${option.frecuencia_valor ? ` (${option.frecuencia_valor} ${option.tipo_contador_nombre || ''})` : ''}`.trim() || `Rutina ${option.id}`}
                            showAllOnFocus
                            size="sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
                    <button onClick={()=>setShowCambioContador(false)} style={{ background: theme.colors.transparent, border:`1px solid ${theme.colors.borderDefault}`, borderRadius:6, padding:'8px 12px', cursor:'pointer' }}>Cancelar</button>
                    <button
                      onClick={registrarCambioContador}
                      disabled={savingCambioContador}
                      style={{ background: theme.colors.primary, color: theme.colors.onPrimary, border:`1px solid ${theme.colors.primary}`, borderRadius:6, padding:'8px 12px', cursor:savingCambioContador ? 'not-allowed' : 'pointer', opacity: savingCambioContador ? 0.7 : 1, fontWeight:600 }}
                    >
                      {savingCambioContador ? 'Guardando...' : 'Registrar cambio'}
                    </button>
                  </div>

                  <div>
                    <div style={{ fontWeight:600, marginBottom:8 }}>Historial de cambios</div>
                    <div style={{ maxHeight:180, overflow:'auto', border:`1px solid ${theme.colors.borderDefault}`, borderRadius:8 }}>
                      {loadingCambiosContador ? (
                        <div style={{ padding:10, fontSize:12 }}>Cargando cambios...</div>
                      ) : cambiosContador.length === 0 ? (
                        <div style={{ padding:10, fontSize:12, color: theme.colors.txtSecondary }}>No hay cambios registrados.</div>
                      ) : (
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign:'left', padding:'6px 10px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.4px', color: theme.colors.txtMuted, borderBottom:`2px solid ${theme.colors.borderDefault}` }}>Fecha</th>
                              <th style={{ textAlign:'right', padding:'6px 10px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.4px', color: theme.colors.txtMuted, borderBottom:`2px solid ${theme.colors.borderDefault}` }}>Salida</th>
                              <th style={{ textAlign:'right', padding:'6px 10px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.4px', color: theme.colors.txtMuted, borderBottom:`2px solid ${theme.colors.borderDefault}` }}>Inicial nuevo</th>
                              <th style={{ textAlign:'right', padding:'6px 10px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.4px', color: theme.colors.txtMuted, borderBottom:`2px solid ${theme.colors.borderDefault}` }}>Acumulado previo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cambiosContador.map((cc) => (
                              <tr key={cc.id}>
                                <td style={{ padding:'7px 10px', fontSize:12, borderBottom:`1px solid ${theme.colors.borderLight}` }}>{require('../../utils/dateUtils').formatDateDisplay(cc.fecha_cambio)}</td>
                                <td style={{ padding:'7px 10px', fontSize:12, textAlign:'right', borderBottom:`1px solid ${theme.colors.borderLight}` }}>{Number(cc.lectura_salida || 0).toLocaleString('es-AR')}</td>
                                <td style={{ padding:'7px 10px', fontSize:12, textAlign:'right', borderBottom:`1px solid ${theme.colors.borderLight}` }}>{Number(cc.lectura_inicial_nueva || 0).toLocaleString('es-AR')}</td>
                                <td style={{ padding:'7px 10px', fontSize:12, textAlign:'right', borderBottom:`1px solid ${theme.colors.borderLight}` }}>{Number(cc.acumulado_previo || 0).toLocaleString('es-AR')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabPane>

        {/* TAB REPUESTOS */}
        <TabPane $active={activeTab === 'repuestos'}>
          <SectionHeader style={{ marginBottom: 10, flexWrap: 'wrap' }}>
            <SectionTitle style={{ fontSize: '1rem' }}><i className="bi bi-box-seam" style={{ color: theme.colors.primary }} /> Repuestos y capacidades</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <HeaderButton
                className={repuestosSubtab === 'recomendados' ? 'btn-primary' : ''}
                onClick={() => setRepuestosSubtab('recomendados')}
              >
                <i className="bi bi-box-seam" /> Repuestos recomendados
              </HeaderButton>
              <HeaderButton
                className={repuestosSubtab === 'capacidades' ? 'btn-primary' : ''}
                onClick={() => setRepuestosSubtab('capacidades')}
              >
                <i className="bi bi-droplet-half" /> Capacidades de llenado
              </HeaderButton>
            </div>
          </SectionHeader>

          {repuestosSubtab === 'recomendados' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
            <div style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: '0.83rem', fontWeight: 700 }}>
                  <i className="bi bi-plus-circle" style={{ color: theme.colors.info }} />
                  Agregar repuesto recomendado
                </strong>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', margin: 0, marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Componente</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <div style={{ flex: 1 }}>
                        <SearchSuggestField
                          value={componenteQuery}
                          onValueChange={(nextValue) => {
                            setComponenteQuery(nextValue);
                            if (!String(nextValue || '').trim()) {
                              setComponenteIdSel('');
                              setRepuestoSel('');
                              setRepuestoQuery('');
                            }
                          }}
                          items={componentesOpts}
                          selectedKey={componenteIdSel}
                          onSelectItem={(item) => {
                            setComponenteIdSel(String(item?.id || ''));
                            setComponenteQuery(item?.nombre || item?.name || '');
                            setRepuestoSel('');
                            setRepuestoQuery('');
                          }}
                          getItemKey={(item) => item?.id}
                          getSearchText={(item) => String(item?.nombre || item?.name || '')}
                          placeholder="Buscar componente..."
                          emptyMessage="Sin componentes"
                          showAllOnFocus={true}
                        />
                      </div>
                      <button
                        type="button"
                        title="Agregar nuevo componente"
                        onClick={() => {
                          setShowModalComponente(true);
                        }}
                        style={compactIconButtonStyle}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div style={{ flex: '1 1 260px' }}>
                    <label style={{ display: 'block', margin: 0, marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Repuesto</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <div style={{ flex: 1 }}>
                        <SearchSuggestField
                          value={repuestoQuery}
                          onValueChange={(nextValue) => {
                            setRepuestoQuery(nextValue);
                            if (!String(nextValue || '').trim()) {
                              setRepuestoSel('');
                            }
                          }}
                          items={repuestosPorComponente}
                          selectedKey={repuestoSel}
                          onSelectItem={(item) => {
                            setRepuestoSel(String(item?.id || ''));
                            setRepuestoQuery(`${item?.codigo || ''}${item?.descripcion ? ` - ${item.descripcion}` : ''}`.trim());
                          }}
                          getItemKey={(item) => item?.id}
                          getSearchText={(item) => `${item?.codigo || ''} ${item?.descripcion || ''}`.trim()}
                          placeholder="Buscar repuesto..."
                          emptyMessage={componenteIdSel ? 'Sin repuestos para este componente' : 'Seleccioná un componente'}
                          disabled={!componenteIdSel}
                          showAllOnFocus={true}
                        />
                      </div>
                      <button
                        type="button"
                        title="Agregar nuevo repuesto"
                        onClick={() => {
                          setShowModalRepuesto(true);
                        }}
                        style={compactIconButtonStyle}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div style={{ width: 110 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Cantidad</label>
                    <input
                      type="number"
                      min="1"
                      value={cantidad}
                      onChange={e => setCantidad(e.target.value)}
                      style={{ width: '100%', height: CONTROL_HEIGHT, padding: '0 8px', background: theme.colors.bgSecundario, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6 }}
                    />
                  </div>

                  <div>
                    <HeaderButton
                      className="btn-primary"
                      onClick={async () => {
                        if (!repuestoSel) { toast.warning('Seleccione un repuesto.'); return; }

                        // Prevenir duplicados (equipo + componente + repuesto)
                        const existe = (repuestosRecomendados || []).some(rr => {
                          const rrComp = rr.componente || null;
                          const selComp = componenteIdSel && componenteIdSel !== '' ? String(componenteIdSel) : null;
                          const rrRepuestoId = rr.repuesto ? (typeof rr.repuesto === 'object' ? String(rr.repuesto.id) : String(rr.repuesto)) : String(rr.repuesto || rr.repuesto_codigo || '');
                          return String(rrRepuestoId) === String(repuestoSel) && String(rrComp) === String(selComp);
                        });

                        if (existe) {
                          toast.warning('Ya existe una recomendación con ese repuesto y componente para este equipo.');
                          return;
                        }

                        setSavingRepuesto(true);
                        try {
                          const payload = {
                            equipo: equipo.id,
                            componente: componenteIdSel || null,
                            repuesto: Number(repuestoSel),
                            cantidad: Number(cantidad) || 1
                          };
                          await apiClient.post('repuestos-recomendados/', payload);
                          setComponenteIdSel('');
                          setComponenteQuery('');
                          setRepuestoSel('');
                          setRepuestoQuery('');
                          setCantidad(1);
                          await cargarRepuestosRecomendados(equipo.id);
                        } catch (err) {
                          console.error('Error asignando repuesto recomendado', err);
                          toast.error(err?.response?.data?.detail || 'No se pudo asignar el repuesto. Intentá nuevamente.');
                        } finally {
                          setSavingRepuesto(false);
                        }
                      }}
                      disabled={savingRepuesto}
                      style={compactActionButtonStyle}
                    >
                      {savingRepuesto ? 'Guardando...' : 'Asignar'}
                    </HeaderButton>
                  </div>

                  <div>
                    <HeaderButton onClick={abrirModalClonado} title="Traer repuestos de otro equipo o copiar los de este a varios equipos" style={compactActionButtonStyle}>
                      <i className="bi bi-copy"></i>
                      Clonar
                    </HeaderButton>
                  </div>
                </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <ListaUnificada
                ref={listaRepuestosRecomendadosRef}
                titulo="Repuestos recomendados"
                icono="bi bi-box-seam"
                sinMarco={true}
                tituloGrande={true}
                tableVisualVariant="rows"
                endpoint={`repuestos-recomendados/?equipo=${equipo.id}`}
                columns={columnsRepuestosRecomendados}
                searchPlaceholder="Buscar por código, marca, descripción o componente…"
                allowAdd={false}
                allowEdit={true}
                allowDelete={true}
                onEdit={(rr) => editarCantidadRepuesto(rr)}
                onDelete={(rr) => eliminarRepuestoRecomendado(rr)}
                resizableColumns={true}
                itemsPerPageOptions={[10, 25, 50]}
                defaultItemsPerPage={10}
              />
            </div>
          </div>
          )}

          {repuestosSubtab === 'capacidades' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
            <div style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: '0.83rem', fontWeight: 700 }}>
                  <i className="bi bi-plus-circle" style={{ color: theme.colors.info }} />
                  Agregar capacidad de llenado
                </strong>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ display: 'block', margin: 0, marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Depósito</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <div style={{ flex: 1 }}>
                        <SearchSuggestField
                          value={capDepositoQuery}
                          onValueChange={(nextValue) => {
                            setCapDepositoQuery(nextValue);
                            if (!String(nextValue || '').trim()) {
                              setCapDepositoSel('');
                              setCapItemSel('');
                              setCapItemQuery('');
                            }
                          }}
                          items={depositosOpts}
                          selectedKey={capDepositoSel}
                          onSelectItem={(item) => {
                            setCapDepositoSel(String(item?.id || ''));
                            setCapDepositoQuery(item?.nombre || item?.name || '');
                            setCapItemSel('');
                            setCapItemQuery('');
                          }}
                          getItemKey={(item) => item?.id}
                          getSearchText={(item) => String(item?.nombre || item?.name || '')}
                          placeholder="Sin depósito"
                          emptyMessage="Sin depósitos"
                          showAllOnFocus={true}
                        />
                      </div>
                      <button
                        type="button"
                        title="Agregar nuevo depósito"
                        onClick={() => setShowModalDeposito(true)}
                        style={compactIconButtonStyle}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div style={{ flex: '1 1 260px' }}>
                    <label style={{ display: 'block', margin: 0, marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Fluido / Combustible</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <div style={{ flex: 1 }}>
                        <SearchSuggestField
                          value={capItemQuery}
                          onValueChange={(nextValue) => {
                            setCapItemQuery(nextValue);
                            if (!String(nextValue || '').trim()) setCapItemSel('');
                          }}
                          items={capItemsOpts}
                          selectedKey={capItemSel}
                          onSelectItem={(item) => {
                            setCapItemSel(item?.key || '');
                            setCapItemQuery(item?.label || '');
                          }}
                          getItemKey={(item) => item?.key}
                          getSearchText={(item) => String(item?.label || '')}
                          placeholder="Buscar fluido o combustible..."
                          emptyMessage={capDepositoSel ? 'Sin fluidos para este depósito' : 'Elegí un depósito para ver sus fluidos, o buscá un combustible'}
                          showAllOnFocus={true}
                        />
                      </div>
                      <button
                        type="button"
                        title="Agregar nuevo tipo de combustible"
                        onClick={() => setShowModalCombustible(true)}
                        style={compactIconButtonStyle}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div style={{ width: 130 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600 }}>Capacidad ({capItemUnidad})</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={capLitros}
                      onChange={e => setCapLitros(e.target.value)}
                      style={{ width: '100%', height: CONTROL_HEIGHT, padding: '0 8px', background: theme.colors.bgSecundario, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6 }}
                    />
                  </div>

                  <div>
                    <HeaderButton
                      className="btn-primary"
                      onClick={guardarCapacidad}
                      disabled={savingCapacidad}
                      style={compactActionButtonStyle}
                    >
                      {savingCapacidad ? 'Guardando...' : 'Agregar'}
                    </HeaderButton>
                  </div>

                  <div>
                    <HeaderButton onClick={abrirModalClonadoCap} title="Traer depósitos de otro equipo o copiar los de este a varios equipos" style={compactActionButtonStyle}>
                      <i className="bi bi-copy"></i>
                      Clonar
                    </HeaderButton>
                  </div>
                </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <ListaUnificada
                ref={listaCapacidadesRef}
                titulo="Capacidades de llenado"
                icono="bi bi-droplet-half"
                sinMarco={true}
                tituloGrande={true}
                tableVisualVariant="rows"
                endpoint={`capacidades-llenado/?equipo=${equipo.id}&ordering=-es_combustible`}
                columns={columnsCapacidadesLlenado}
                searchPlaceholder="Buscar por fluido o depósito…"
                allowAdd={false}
                allowEdit={true}
                allowDelete={true}
                onEdit={(cap) => editarCapacidadLitros(cap)}
                onDelete={(cap) => eliminarCapacidad(cap.id)}
                resizableColumns={true}
                itemsPerPageOptions={[10, 25, 50]}
                defaultItemsPerPage={10}
              />
            </div>
          </div>
          )}
        </TabPane>

        {/* TAB PARTES */}
        <TabPane $active={activeTab === 'partes'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
              <ListaUnificada
                ref={listaPartesRef}
                titulo="Partes Diarios"
                icono={tabs.find(t => t.id === 'partes')?.icon}
                sinMarco={true}
                tituloGrande={true}
                endpoint={`partes-diarios/?equipo__id=${equipo.id}`}
                tableVisualVariant="rows"
                columns={columnsPartes}
                searchPlaceholder="Buscar partes diarios..."
                allowAdd={true}
                allowEdit={true}
                allowDelete={true}
                onAdd={() => { setEditingParte(null); setShowParteModal(true); }}
                onEdit={(parte) => { setEditingParte(parte); setShowParteModal(true); }}
                onDelete={handleDeleteParte}
                resizableColumns={true}
                itemsPerPageOptions={[10,25,50]}
                defaultItemsPerPage={10}
              />
            </div>
          </div>
        </TabPane>

        {/* TAB REPARACIONES */}
        <TabPane $active={activeTab === 'reparaciones'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
              <ListaUnificada
                ref={listaOtcRef}
                titulo="Órdenes Correctivas"
                icono={tabs.find(t => t.id === 'reparaciones')?.icon}
                sinMarco={true}
                tituloGrande={true}
                tableVisualVariant="rows"
                endpoint={`ordenes-trabajo-correctivas/?equipo__id=${equipo.id}`}
                columns={columnsReparaciones}
                searchPlaceholder="Buscar OTC por número o título..."
                allowAdd={true}
                allowEdit={true}
                allowDelete={true}
                onAdd={() => navigate(`/reparaciones/otc/nueva?equipo=${equipo.id}`)}
                onEdit={(row) => navigate(`/reparaciones/otc/${row.id}`)}
                onDelete={handleCancelarOtc}
                resizableColumns={true}
                itemsPerPageOptions={[10,25,50]}
                defaultItemsPerPage={10}
              />
            </div>
          </div>
        </TabPane>

        {/* TAB LUBRICANTES */}
        <TabPane $active={activeTab === 'lubricantes'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
              <ListaUnificada
                ref={listaLubricantesRef}
                titulo="Registro de Lubricantes"
                icono={tabs.find(t => t.id === 'lubricantes')?.icon}
                sinMarco={true}
                tituloGrande={true}
                endpoint={`lubricantes/?equipo__id=${equipo.id}`}
                tableVisualVariant="rows"
                columns={columnsLubricantes}
                searchPlaceholder="Buscar registros de lubricantes..."
                allowAdd={true}
                allowEdit={true}
                allowDelete={true}
                onAdd={() => { setEditingLubricante(null); setShowLubricanteModal(true); }}
                onEdit={(registro) => { setEditingLubricante(registro); setShowLubricanteModal(true); }}
                onDelete={handleDeleteLubricante}
                resizableColumns={true}
                itemsPerPageOptions={[10,25,50]}
                defaultItemsPerPage={10}
              />
            </div>
          </div>
        </TabPane>

        {/* TAB DOCUMENTACIÓN */}
        <TabPane $active={activeTab === 'documentacion'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
              <ListaUnificada
                ref={listaDocumentacionRef}
                titulo="Documentación"
                icono={tabs.find(t => t.id === 'documentacion')?.icon}
                sinMarco={true}
                tituloGrande={true}
                endpoint={`documentacion/?equipo__id=${equipo.id}`}
                tableVisualVariant="rows"
                columns={columnsDocumentacion}
                searchPlaceholder="Buscar documentación..."
                allowAdd={true}
                onAdd={() => { setEditingDocumento(null); setShowDocModal(true); }}
                allowEdit={true}
                allowDelete={true}
                onEdit={(documento) => { setEditingDocumento(documento); setShowDocModal(true); }}
                onDelete={handleDeleteDocumento}
                resizableColumns={true}
                itemsPerPageOptions={[10,25,50]}
                defaultItemsPerPage={10}
              />
            </div>
          </div>
        </TabPane>

        {/* TAB ACEITES removida: se unifica en TAB LUBRICANTES */}

        {/* TAB CHECK LISTS */}
        <TabPane $active={activeTab === 'checklist'}>
          <CheckListTab equipoId={equipo?.id} contadorNombre={equipo?.contador_nombre} familiaId={equipo?.familia} />
        </TabPane>

        {/* TAB TRASLADOS */}
        <TabPane $active={activeTab === 'traslados'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
              <ListaUnificada
                titulo="Historial de Traslados"
                icono={tabs.find(t => t.id === 'traslados')?.icon}
                sinMarco={true}
                tituloGrande={true}
                endpoint={`movimientos-logisticos/?equipos_transportados__equipo=${equipo?.id}&ordering=-fecha_programada`}
                tableVisualVariant="rows"
                searchPlaceholder="Buscar traslados..."
                allowAdd={false}
                allowEdit={false}
                allowDelete={false}
                allowView={true}
                onView={(row) => navigate(`/logistica/${row.id}`)}
                itemsPerPageOptions={[10, 25, 50]}
                defaultItemsPerPage={10}
                columns={[
                  {
                    key: 'fecha_ejecucion',
                    label: 'Fecha',
                    sortable: true,
                    sortKey: 'fecha_ejecucion',
                    width: '10%',
                    render: (val, row) => require('../../utils/dateUtils').formatDateDisplay(val || row?.fecha_programada),
                  },
                  {
                    key: 'numero_movimiento',
                    label: 'N° Movimiento',
                    sortable: true,
                    sortKey: 'numero_movimiento',
                    width: '12%',
                  },
                  {
                    key: 'origen_nombre',
                    label: 'Origen',
                    sortable: false,
                    render: (val) => val || '-',
                  },
                  {
                    key: 'destino_nombre',
                    label: 'Destino',
                    sortable: false,
                    render: (val) => val || '-',
                  },
                  {
                    key: 'equipo_tractor_interno',
                    label: 'Unidad',
                    sortable: false,
                    width: '9%',
                    render: (val) => val || '-',
                  },
                  {
                    key: 'nombre_chofer',
                    label: 'Chofer',
                    sortable: false,
                    render: (val) => val || '-',
                  },
                  {
                    key: 'patente_camion',
                    label: 'Patente',
                    sortable: false,
                    width: '9%',
                    render: (val) => val || '-',
                  },
                  {
                    key: 'estado',
                    label: 'Estado',
                    sortable: true,
                    sortKey: 'estado',
                    width: '11%',
                    render: (val) => {
                      const etiquetas = { programado: 'Programado', en_ejecucion: 'En Ejecución', finalizado: 'Finalizado', cancelado: 'Cancelado' };
                      return etiquetas[val] || val || '-';
                    },
                  },
                ]}
              />
            </div>
          </div>
        </TabPane>

      </TabContent>


      {showEditRepuestoModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: theme.colors.overlayBackdropSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }}
          onMouseDown={editRepuestoBackdropGuard.onBackdropMouseDown}
          onClick={editRepuestoBackdropGuard.buildOnBackdropClick(
            () => {
              setShowEditRepuestoModal(false);
              setEditingRepuesto(null);
            },
            () => !savingEditRepuesto,
          )}
        >
          <div
            style={{ width: 'min(420px, 94vw)', background: theme.colors.bgPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 10, boxShadow: theme.shadows.lg }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.colors.borderDefault}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ color: theme.colors.txtPrincipal }}>Editar repuesto recomendado</strong>
              <button
                onClick={() => {
                  if (!savingEditRepuesto) {
                    setShowEditRepuestoModal(false);
                    setEditingRepuesto(null);
                  }
                }}
                style={{ background: theme.colors.transparent, border: `1px solid ${theme.colors.borderDefault}`, color: theme.colors.txtPrincipal, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{
                marginBottom: 0,
                color: theme.colors.txtPrincipal,
                fontSize: 18,
                fontWeight: 700,
                textAlign: 'center',
                lineHeight: 1.25,
                letterSpacing: 0.2,
                overflowWrap: 'anywhere',
                minHeight: 28
              }}>
                {[editingRepuesto?.repuesto_codigo, editingRepuesto?.repuesto_marca, editingRepuesto?.repuesto_descripcion].filter(Boolean).join(' - ') || '-'}
                {editingRepuesto?.repuesto_unidad_medida ? ` (${editingRepuesto.repuesto_unidad_medida})` : ''}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                <label style={{ fontSize: 14, fontWeight: 600, margin: 0, color: theme.colors.txtSecondary }}>Cantidad</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={editCantidadRepuesto}
                  onChange={(e) => setEditCantidadRepuesto(e.target.value)}
                  style={{
                    width: 70,
                    padding: '7px 8px',
                    background: theme.colors.bgSecundario,
                    color: theme.colors.txtPrincipal,
                    border: `1px solid ${theme.colors.borderDefault}`,
                    borderRadius: 6,
                    fontSize: 16,
                    textAlign: 'center',
                    fontWeight: 600,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ padding: 14, borderTop: `1px solid ${theme.colors.borderDefault}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => {
                  if (!savingEditRepuesto) {
                    setShowEditRepuestoModal(false);
                    setEditingRepuesto(null);
                  }
                }}
                style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={guardarEdicionRepuesto}
                disabled={savingEditRepuesto}
                style={{ background: theme.colors.primary, color: theme.colors.onPrimary, border: `1px solid ${theme.colors.primary}`, borderRadius: 6, padding: '8px 12px', cursor: savingEditRepuesto ? 'not-allowed' : 'pointer', opacity: savingEditRepuesto ? 0.7 : 1 }}
              >
                {savingEditRepuesto ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditCapacidadModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: theme.colors.overlayBackdropSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }}
          onMouseDown={editCapacidadBackdropGuard.onBackdropMouseDown}
          onClick={editCapacidadBackdropGuard.buildOnBackdropClick(
            () => {
              setShowEditCapacidadModal(false);
              setEditingCapacidad(null);
            },
            () => !savingEditCapacidad,
          )}
        >
          <div
            style={{ width: 'min(420px, 94vw)', background: theme.colors.bgPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 10, boxShadow: theme.shadows.lg }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.colors.borderDefault}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ color: theme.colors.txtPrincipal }}>Editar capacidad de llenado</strong>
              <button
                onClick={() => {
                  if (!savingEditCapacidad) {
                    setShowEditCapacidadModal(false);
                    setEditingCapacidad(null);
                  }
                }}
                style={{ background: theme.colors.transparent, border: `1px solid ${theme.colors.borderDefault}`, color: theme.colors.txtPrincipal, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{
                marginBottom: 0,
                color: theme.colors.txtPrincipal,
                fontSize: 18,
                fontWeight: 700,
                textAlign: 'center',
                lineHeight: 1.25,
                letterSpacing: 0.2,
                overflowWrap: 'anywhere',
                minHeight: 28
              }}>
                {editingCapacidad?.fluido_nombre || '-'}
                {editingCapacidad?.deposito_nombre ? ` (${editingCapacidad.deposito_nombre})` : ''}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                <label style={{ fontSize: 14, fontWeight: 600, margin: 0, color: theme.colors.txtSecondary }}>Capacidad ({editingCapacidad?.fluido_unidad_medida || 'L'})</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editCapacidadLitros}
                  onChange={(e) => setEditCapacidadLitros(e.target.value)}
                  style={{
                    width: 90,
                    padding: '7px 8px',
                    background: theme.colors.bgSecundario,
                    color: theme.colors.txtPrincipal,
                    border: `1px solid ${theme.colors.borderDefault}`,
                    borderRadius: 6,
                    fontSize: 16,
                    textAlign: 'center',
                    fontWeight: 600,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ padding: 14, borderTop: `1px solid ${theme.colors.borderDefault}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => {
                  if (!savingEditCapacidad) {
                    setShowEditCapacidadModal(false);
                    setEditingCapacidad(null);
                  }
                }}
                style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={guardarEdicionCapacidad}
                disabled={savingEditCapacidad}
                style={{ background: theme.colors.primary, color: theme.colors.onPrimary, border: `1px solid ${theme.colors.primary}`, borderRadius: 6, padding: '8px 12px', cursor: savingEditCapacidad ? 'not-allowed' : 'pointer', opacity: savingEditCapacidad ? 0.7 : 1 }}
              >
                {savingEditCapacidad ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

        {showClonadoModal && (
          <div
            style={{ position: 'fixed', inset: 0, background: theme.colors.overlayBackdropSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}
            onMouseDown={clonadoBackdropGuard.onBackdropMouseDown}
            onClick={clonadoBackdropGuard.buildOnBackdropClick(() => setShowClonadoModal(false))}
          >
            <div
              style={{ width: modoClonado === 'enviar' ? 'min(1200px, 96vw)' : 'min(960px, 96vw)', maxHeight: '88vh', background: theme.colors.bgPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 10, boxShadow: theme.shadows.lg, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.colors.borderDefault}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ color: theme.colors.txtPrincipal }}>Clonar repuestos recomendados</strong>
                  <div style={{ color: theme.colors.txtSecondary, fontSize: 12, marginTop: 3 }}>
                    {modoClonado === 'enviar'
                      ? `Equipo origen: ${equipo?.interno || '-'}`
                      : `Equipo destino: ${equipo?.interno || '-'}`}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {renderModoClonado('repuestos')}
                  <button onClick={() => setShowClonadoModal(false)} style={{ background: theme.colors.transparent, border: `1px solid ${theme.colors.borderDefault}`, color: theme.colors.txtPrincipal, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                    Cerrar
                  </button>
                </div>
              </div>

              <div style={{ padding: 14, flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: modoClonado === 'enviar' ? 'minmax(0, 1.6fr) minmax(300px, 1fr)' : '1fr', gap: 12, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: 10 }}>
                  {modoClonado === 'traer' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6 }}>Equipo origen (buscar por interno)</label>
                        <SearchSuggestField
                          value={equipoOrigenClonadoQuery}
                          onValueChange={(nextValue) => {
                            setEquipoOrigenClonadoQuery(nextValue);
                            if (!String(nextValue || '').trim()) {
                              setEquipoOrigenClonadoSel('');
                              setRepuestosOrigen([]);
                              setSelectedCloneIds([]);
                            }
                          }}
                          items={equiposParaClonar}
                          selectedKey={equipoOrigenClonadoSel}
                          onSelectItem={(item) => {
                            const nextId = String(item?.id || '');
                            setEquipoOrigenClonadoSel(nextId);
                            setEquipoOrigenClonadoQuery(labelEquipoClonado(item));
                            cargarRepuestosOrigen(nextId);
                          }}
                          getItemKey={(item) => item?.id}
                          getSearchText={(item) => labelEquipoClonado(item)}
                          placeholder={loadingEquiposClonacion ? 'Cargando equipos...' : 'Buscar equipo origen...'}
                          emptyMessage="Sin equipos disponibles"
                          disabled={loadingEquiposClonacion}
                          showAllOnFocus={true}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={seleccionarTodosClonado} disabled={itemsClonadoRepuestos.length === 0} style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}>
                          Seleccionar todos
                        </button>
                        <button onClick={limpiarSeleccionClonado} disabled={selectedCloneIds.length === 0} style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}>
                          Limpiar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ color: theme.colors.txtPrincipal, fontSize: 13 }}>Repuestos a copiar</strong>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={seleccionarTodosClonado} disabled={itemsClonadoRepuestos.length === 0} style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}>
                          Seleccionar todos
                        </button>
                        <button onClick={limpiarSeleccionClonado} disabled={selectedCloneIds.length === 0} style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}>
                          Limpiar
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ color: theme.colors.txtSecondary, fontSize: 13 }}>
                    {modoClonado === 'enviar'
                      ? `Origen: ${resumenClonado.totalOrigen} | Seleccionados: ${resumenClonado.seleccionados} | Los repuestos ya cargados en cada destino se omiten`
                      : `Origen: ${resumenClonado.totalOrigen} | Seleccionados: ${resumenClonado.seleccionados} | Posibles duplicados en destino: ${resumenClonado.duplicados}`}
                  </div>

                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                    {loadingRepuestosOrigen ? (
                      <div style={{ color: theme.colors.txtSecondary }}>Cargando repuestos del equipo origen...</div>
                    ) : itemsClonadoRepuestos.length === 0 ? (
                      <div style={{ color: theme.colors.txtSecondary }}>
                        {modoClonado === 'enviar'
                          ? 'Este equipo todavía no tiene repuestos recomendados para copiar.'
                          : 'Seleccione un equipo origen para ver y elegir los repuestos a clonar.'}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ position: 'sticky', top: 0, background: theme.colors.bgPrincipal, zIndex: 1 }}>
                          <tr>
                            <th style={{ textAlign: 'center', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}`, width: 60 }}>Sel</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}` }}>Código</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}` }}>Marca</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}` }}>Descripción</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}` }}>Componente</th>
                            <th style={{ textAlign: 'center', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}`, width: 100 }}>Cantidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsClonadoRepuestos.map((item) => {
                            const checked = selectedCloneIds.includes(item.id);
                            const esDuplicadoDestino = modoClonado === 'traer' && repuestosDestinoSet.has(buildClaveRepuesto(item));
                            return (
                              <tr key={item.id} style={{ borderBottom: `1px solid ${theme.colors.borderLight}` }}>
                                <td style={{ textAlign: 'center', padding: '7px 10px' }}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleSeleccionClonado(item.id)} />
                                </td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtPrincipal }}>{item.repuesto_codigo || '-'}</td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtSecondary }}>{item.repuesto_marca || '-'}</td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtSecondary }}>{item.repuesto_descripcion || '-'}</td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtSecondary }}>
                                  {item.componente_nombre || 'Sin componente'}
                                  {esDuplicadoDestino && (
                                    <span style={{ marginLeft: 8, fontSize: 11, color: theme.colors.warning }}>Ya existe en destino</span>
                                  )}
                                </td>
                                <td style={{ textAlign: 'center', padding: '7px 10px', fontSize: 12, color: theme.colors.txtPrincipal }}>{item.cantidad}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {modoClonado === 'enviar' && renderPanelDestinos()}
              </div>

              <div style={{ padding: 14, borderTop: `1px solid ${theme.colors.borderDefault}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setShowClonadoModal(false)} style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button
                  onClick={ejecutarClonadoRepuestos}
                  disabled={clonandoRepuestos || selectedCloneIds.length === 0 || (modoClonado === 'enviar' && equiposDestinoSel.length === 0)}
                  style={{ background: theme.colors.primary, color: theme.colors.onPrimary, border: `1px solid ${theme.colors.primary}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                >
                  {clonandoRepuestos
                    ? 'Clonando...'
                    : (modoClonado === 'enviar'
                      ? `Copiar a ${equiposDestinoSel.length} equipo(s)`
                      : 'Clonar seleccionados')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de clonado de CAPACIDADES DE LLENADO */}
        {showClonadoCapModal && (
          <div
            style={{ position: 'fixed', inset: 0, background: theme.colors.overlayBackdropSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}
            onMouseDown={clonadoBackdropGuard.onBackdropMouseDown}
            onClick={clonadoBackdropGuard.buildOnBackdropClick(() => setShowClonadoCapModal(false))}
          >
            <div
              style={{ width: modoClonado === 'enviar' ? 'min(1200px, 96vw)' : 'min(960px, 96vw)', maxHeight: '88vh', background: theme.colors.bgPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 10, boxShadow: theme.shadows.lg, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.colors.borderDefault}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ color: theme.colors.txtPrincipal }}>Clonar capacidades de llenado</strong>
                  <div style={{ color: theme.colors.txtSecondary, fontSize: 12, marginTop: 3 }}>
                    {modoClonado === 'enviar'
                      ? `Equipo origen: ${equipo?.interno || '-'}`
                      : `Equipo destino: ${equipo?.interno || '-'}`}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {renderModoClonado('capacidades')}
                  <button onClick={() => setShowClonadoCapModal(false)} style={{ background: theme.colors.transparent, border: `1px solid ${theme.colors.borderDefault}`, color: theme.colors.txtPrincipal, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                    Cerrar
                  </button>
                </div>
              </div>

              <div style={{ padding: 14, flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: modoClonado === 'enviar' ? 'minmax(0, 1.6fr) minmax(300px, 1fr)' : '1fr', gap: 12, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: 10 }}>
                  {modoClonado === 'traer' ? (
                    <div>
                      <label style={{ display: 'block', marginBottom: 6 }}>Equipo origen (buscar por interno)</label>
                      <SearchSuggestField
                        value={equipoOrigenClonadoQuery}
                        onValueChange={(nextValue) => {
                          setEquipoOrigenClonadoQuery(nextValue);
                          if (!String(nextValue || '').trim()) {
                            setEquipoOrigenClonadoSel('');
                            setCapacidadesOrigen([]);
                            setSelectedCloneCapIds([]);
                          }
                        }}
                        items={equiposParaClonar}
                        selectedKey={equipoOrigenClonadoSel}
                        onSelectItem={(item) => {
                          const nextId = String(item?.id || '');
                          setEquipoOrigenClonadoSel(nextId);
                          setEquipoOrigenClonadoQuery(labelEquipoClonado(item));
                          cargarCapacidadesOrigen(nextId);
                        }}
                        getItemKey={(item) => item?.id}
                        getSearchText={(item) => labelEquipoClonado(item)}
                        placeholder={loadingEquiposClonacion ? 'Cargando equipos...' : 'Buscar equipo origen...'}
                        emptyMessage="Sin equipos disponibles"
                        disabled={loadingEquiposClonacion}
                        showAllOnFocus={true}
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ color: theme.colors.txtPrincipal, fontSize: 13 }}>Depósitos a copiar</strong>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => setSelectedCloneCapIds(itemsClonadoCapacidades.map((item) => item.id))}
                          disabled={itemsClonadoCapacidades.length === 0}
                          style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}
                        >
                          Seleccionar todos
                        </button>
                        <button
                          onClick={() => setSelectedCloneCapIds([])}
                          disabled={selectedCloneCapIds.length === 0}
                          style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '7px 10px', cursor: 'pointer' }}
                        >
                          Limpiar
                        </button>
                      </div>
                    </div>
                  )}

                  {modoClonado === 'enviar' && (
                    <div style={{ color: theme.colors.txtSecondary, fontSize: 13 }}>
                      Origen: {itemsClonadoCapacidades.length} | Seleccionados: {selectedCloneCapIds.length} | Los depósitos ya cargados en cada destino se omiten
                    </div>
                  )}

                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                    {loadingCapacidadesOrigen ? (
                      <div style={{ color: theme.colors.txtSecondary }}>Cargando capacidades del equipo origen...</div>
                    ) : itemsClonadoCapacidades.length === 0 ? (
                      <div style={{ color: theme.colors.txtSecondary }}>
                        {modoClonado === 'enviar'
                          ? 'Este equipo todavía no tiene depósitos cargados para copiar.'
                          : 'Seleccione un equipo origen para ver y elegir las capacidades a clonar.'}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ position: 'sticky', top: 0, background: theme.colors.bgPrincipal, zIndex: 1 }}>
                          <tr>
                            <th style={{ textAlign: 'center', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}`, width: 60 }}>Sel</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}` }}>Fluido / Combustible</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}` }}>Depósito</th>
                            <th style={{ textAlign: 'center', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}`, width: 90 }}>Capacidad</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: theme.colors.txtMuted, borderBottom: `2px solid ${theme.colors.borderDefault}`, width: 80 }}>Unidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsClonadoCapacidades.map((item) => {
                            const checked = selectedCloneCapIds.includes(item.id);
                            const esDuplicado = modoClonado === 'traer' && capacidadesDestinoSet.has(buildClaveCapacidad(item));
                            return (
                              <tr key={item.id} style={{ borderBottom: `1px solid ${theme.colors.borderLight}` }}>
                                <td style={{ textAlign: 'center', padding: '7px 10px' }}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleSeleccionClonadoCap(item.id)} />
                                </td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtPrincipal }}>{item.fluido_nombre || '-'}</td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtSecondary }}>
                                  {item.deposito_nombre || 'Sin depósito'}
                                  {esDuplicado && (
                                    <span style={{ marginLeft: 8, fontSize: 11, color: theme.colors.warning }}>Ya existe en destino</span>
                                  )}
                                </td>
                                <td style={{ textAlign: 'center', padding: '7px 10px', fontSize: 12, color: theme.colors.txtPrincipal }}>{item.capacidad_litros}</td>
                                <td style={{ padding: '7px 10px', fontSize: 12, color: theme.colors.txtSecondary }}>{item.fluido_unidad_medida || 'L'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {modoClonado === 'enviar' && renderPanelDestinos()}
              </div>

              <div style={{ padding: 14, borderTop: `1px solid ${theme.colors.borderDefault}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button onClick={() => setShowClonadoCapModal(false)} style={{ background: theme.colors.transparent, color: theme.colors.txtPrincipal, border: `1px solid ${theme.colors.borderDefault}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button
                  onClick={ejecutarClonadoCapacidades}
                  disabled={clonandoCapacidades || selectedCloneCapIds.length === 0 || (modoClonado === 'enviar' && equiposDestinoSel.length === 0)}
                  style={{ background: theme.colors.primary, color: theme.colors.onPrimary, border: `1px solid ${theme.colors.primary}`, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                >
                  {clonandoCapacidades
                    ? 'Clonando...'
                    : (modoClonado === 'enviar'
                      ? `Copiar a ${equiposDestinoSel.length} equipo(s)`
                      : 'Clonar seleccionadas')}
                </button>
              </div>
            </div>
          </div>
        )}

      {showParteModal && (
        <ModalParteDiario
          isOpen={showParteModal}
          onClose={() => { setShowParteModal(false); setEditingParte(null); }}
          parteDiario={editingParte || null}
          initialEquipo={editingParte ? undefined : equipo.id}
          onSaved={() => {
            setEditingParte(null);
            refreshLista(listaPartesRef);
          }}
        />
      )}

      {showLubricanteModal && (
        <ModalRegistroLubricante
          isOpen={showLubricanteModal}
          onClose={() => { setShowLubricanteModal(false); setEditingLubricante(null); }}
          registro={editingLubricante || null}
          initialEquipo={editingLubricante ? undefined : equipo.id}
          onSaved={() => {
            setEditingLubricante(null);
            refreshLista(listaLubricantesRef);
          }}
        />
      )}

      {showDocModal && (
        <ModalDocumentacion
          isOpen={showDocModal}
          onClose={() => { setShowDocModal(false); setEditingDocumento(null); }}
          documento={editingDocumento || null}
          initialEquipo={editingDocumento ? undefined : equipo.id}
          onSaved={() => {
            setEditingDocumento(null);
            refreshLista(listaDocumentacionRef);
          }}
        />
      )}

      {documentoAdjuntos && (
        <ModalAdjuntosDocumentacion
          isOpen={Boolean(documentoAdjuntos)}
          onClose={() => setDocumentoAdjuntos(null)}
          documento={documentoAdjuntos}
          onChanged={() => refreshLista(listaDocumentacionRef)}
        />
      )}

      {showModalComponente && (
        <ConfiguracionBase
          apiEndpoint="componentes"
          titulo="Componentes"
          descripcion="Componentes de equipos organizados por sistema"
          icon="fas fa-puzzle-piece"
          embedded
          compact
          modalOnly
          autoOpenAdd
          onRecordSaved={handleComponenteRecordSaved}
          onRequestClose={() => setShowModalComponente(false)}
        />
      )}

      {showModalRepuesto && (
        <ConfiguracionBase
          apiEndpoint="repuestos"
          titulo="Repuestos"
          descripcion="Inventario de repuestos"
          icon="fas fa-tools"
          embedded
          compact
          modalOnly
          autoOpenAdd
          onRecordSaved={handleRepuestoRecordSaved}
          onRequestClose={() => setShowModalRepuesto(false)}
        />
      )}

      {showModalDeposito && (
        <ConfiguracionBase
          apiEndpoint="depositos"
          titulo="Depósitos"
          descripcion="Depósitos de fluidos de los equipos"
          icon="fas fa-fill-drip"
          embedded
          compact
          modalOnly
          autoOpenAdd
          onRecordSaved={handleDepositoRecordSaved}
          onRequestClose={() => setShowModalDeposito(false)}
        />
      )}

      {showModalCombustible && (
        <ConfiguracionBase
          apiEndpoint="tipos-combustible"
          titulo="Tipos de combustible"
          descripcion="Gasoil, nafta, etc."
          icon="fas fa-gas-pump"
          embedded
          compact
          modalOnly
          autoOpenAdd
          onRecordSaved={handleCombustibleRecordSaved}
          onRequestClose={() => setShowModalCombustible(false)}
        />
      )}

      {showMapaGPS && (
        <ModalMapaGPSEquipo
          equipoId={equipo?.id}
          equipoNombre={equipo?.interno || equipo?.nombre}
          onClose={() => setShowMapaGPS(false)}
        />
      )}
    </DetalleContainer>
  );
};

// ─── CheckListTab ────────────────────────────────────────────────────────────

// ── Modal nuevo check list ──

const CLModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdrop};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const CLModalCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 12px;
  width: min(400px, 92vw);
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
  overflow: hidden;
`;

const CLModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const CLModalTitle = styled.span`
  font-weight: 700;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const CLModalClose = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.txtSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  transition: background-color 0.12s ease;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

const CLModalBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CLTipoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
`;

const CLTipoCard = styled.button`
  background: ${({ theme, $active }) => $active ? theme.colors.primaryAlpha : theme.colors.bgSecundario};
  border: 1.5px solid ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.borderDefault};
  border-radius: 8px;
  padding: 0.9rem 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  transition-property: background-color, border-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryAlpha};
  }
`;

const CLTipoIcon = styled.div`
  font-size: 1.6rem;
  line-height: 1;
`;

const CLTipoLabel = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.txtPrincipal};
`;

const CLTipoDesc = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  text-align: center;
  line-height: 1.3;
`;

const CLModalFooter = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const CLModalPrimaryBtn = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: background-color 0.15s ease;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.primaryHover}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const CLModalCancelBtn = styled.button`
  background: none;
  color: ${({ theme }) => theme.colors.txtSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px;
  padding: 0.45rem 0.8rem;
  font-size: 0.82rem;
  cursor: pointer;
  transition: background-color 0.12s ease;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

const CLBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;
`;

const CLTipoBadge = styled(CLBadge)`
  text-transform: uppercase;
  background: ${({ $tipo, theme }) => $tipo === 'ingreso' ? theme.colors.primaryAlpha : theme.colors.accentAlpha};
  color: ${({ $tipo, theme }) => $tipo === 'ingreso' ? theme.colors.primary : theme.colors.accent};
`;

const CLResultBadge = styled(CLBadge)`
  background: ${({ $res, theme }) =>
    $res === 'operativo' ? theme.colors.successAlpha : theme.colors.warningAlpha};
  color: ${({ $res, theme }) =>
    $res === 'operativo' ? theme.colors.success : theme.colors.warning};
  border: 1px solid ${({ $res, theme }) =>
    $res === 'operativo' ? theme.colors.successSoftBorder : theme.colors.warningSoftBorder};
`;

const CLBorradorBadge = styled(CLBadge)`
  color: ${({ theme }) => theme.colors.txtSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const CLOtcBadge = styled(CLBadge)`
  background: ${({ theme }) => theme.colors.naranjaAlpha};
  color: ${({ theme }) => theme.colors.naranja};
  border: 1px solid ${({ theme }) => theme.colors.naranjaSoftBorder};
  cursor: pointer;
  transition-property: background-color, border-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  &:hover {
    background: ${({ theme }) => theme.colors.naranjaAlphaHover};
    border-color: ${({ theme }) => theme.colors.naranjaSoftBorderHover};
  }
`;

const CLId = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  opacity: ${({ $borrador }) => $borrador ? 0.5 : 1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;

const CLCell = styled.span`
  font-size: 0.78rem;
  color: ${({ $muted, theme }) => $muted ? theme.colors.txtSecondary : theme.colors.txtPrincipal};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  text-align: center;
`;

const CLFallas = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.danger};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
`;

const CLChevron = styled.i`
  opacity: 0.3;
  font-size: 0.72rem;
  flex-shrink: 0;
`;

const formatChlId = (id) => `CHL-${String(id).padStart(9, '0')}`;

const CLEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.txtSecondary};
  i { font-size: 1.6rem; opacity: 0.3; margin-bottom: 0.4rem; }
  p { margin: 0; font-size: 0.82rem; line-height: 1.5; color: ${({ theme }) => theme.colors.txtPrincipal}; }
  small { font-size: 0.75rem; opacity: 0.6; margin-top: 0.15rem; }
`;

const CLNoPlantillaNudge = styled.div`
  margin: 1.25rem 1rem;
  padding: 1.1rem 1.25rem 1.25rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  text-align: center;

  .nudge-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.primaryAlpha};
    border: 1px solid ${({ theme }) => theme.colors.primarySoftBorder};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.2rem;
    i { font-size: 1.1rem; color: ${({ theme }) => theme.colors.primary}; opacity: 0.8; }
  }

  .nudge-title {
    font-size: 0.82rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.txtPrincipal};
    margin: 0;
  }

  .nudge-sub {
    font-size: 0.74rem;
    color: ${({ theme }) => theme.colors.txtSecondary};
    line-height: 1.45;
    margin: 0;
  }
`;

const CLConfigBtn = styled.button`
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.42rem 1.1rem;
  font-size: 0.79rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  transition-property: background-color, transform;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;
  &:hover { background: ${({ theme }) => theme.colors.primaryHover}; }
  &:active { transform: scale(0.96); }
`;

const CheckListTab = ({ equipoId, contadorNombre, familiaId }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirmar } = useConfirm();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const [plantillaExiste, setPlantillaExiste] = useState(null);
  const [plantillaId, setPlantillaId] = useState(null);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [tipoNuevo, setTipoNuevo] = useState('ingreso');
  const [iniciando, setIniciando] = useState(false);

  useEffect(() => {
    if (!equipoId) return;
    setPlantillaExiste(null);
    setPlantillaId(null);
    if (!familiaId) {
      setPlantillaExiste(false);
      return;
    }
    let cancelado = false;
    (async () => {
      const pts = await checkListService.listarPlantillas({ familia: familiaId });
      const arr = Array.isArray(pts) ? pts : pts?.results || [];
      if (cancelado) return;
      setPlantillaExiste(arr.length > 0);
      if (arr[0]?.id) setPlantillaId(arr[0].id);
    })();
    return () => { cancelado = true; };
  }, [equipoId, familiaId]);

  const handleIniciarNuevo = async () => {
    if (!equipoId) return;
    setIniciando(true);
    try {
      const pts = await checkListService.listarPlantillas({ familia: familiaId });
      const arr = Array.isArray(pts) ? pts : pts?.results || [];
      const plantillaId = arr[0]?.id;
      if (!plantillaId) {
        toast.warning('Esta familia no tiene una plantilla de check list configurada. Andá a Configuración → Check List de Equipos para crearla.');
        return;
      }
      const cl = await checkListService.crear({ equipo: parseInt(equipoId), plantilla: plantillaId, tipo: tipoNuevo });
      navigate(`/equipos/${equipoId}/checklist/${cl.id}`);
    } finally {
      setIniciando(false);
    }
  };

  const handleEliminarCheckList = async (item, index, performDelete) => {
    const ok = await confirmar({
      titulo: '¿Eliminar check list?',
      mensaje: `Se eliminará el check list ${formatChlId(item.id)} (${item.tipo}). Esta acción no se puede deshacer.`,
      labelConfirmar: 'Sí, eliminar',
      variante: 'danger',
    });
    if (!ok) return false;
    try {
      await performDelete();
      toast.success('Check list eliminado.');
      return true;
    } catch (e) {
      console.error('Error al eliminar check list', e);
      toast.error('No se pudo eliminar el check list. Intentá nuevamente o contactá al soporte.');
      return true; // evitar que ListaUnificada reintente el borrado por su cuenta
    }
  };

  const columnsCheckList = [
    {
      key: 'tipo',
      label: 'Motivo',
      width: '90px',
      render: (val) => <CLTipoBadge $tipo={val}>{val}</CLTipoBadge>,
    },
    {
      key: 'id',
      label: 'Check List',
      width: '130px',
      render: (val, row) => <CLId $borrador={row.estado === 'borrador'}>{formatChlId(val)}</CLId>,
    },
    {
      key: 'fecha_creacion',
      label: 'Fecha',
      width: '90px',
      sortable: true,
      render: (val) => <CLCell $muted>{new Date(val).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</CLCell>,
    },
    {
      key: 'horas_km',
      label: contadorNombre || 'Lectura',
      width: '100px',
      render: (val) => <CLCell $muted>{val != null ? `${val} ${contadorNombre || ''}`.trim() : '—'}</CLCell>,
    },
    {
      key: 'origen_nombre',
      label: 'Origen',
      render: (val) => <CLCell title={val}>{val || '—'}</CLCell>,
    },
    {
      key: 'destino_nombre',
      label: 'Destino',
      render: (val) => <CLCell title={val}>{val || '—'}</CLCell>,
    },
    {
      key: 'estado',
      label: 'Estado',
      width: '110px',
      render: (val, row) => {
        if (val === 'borrador') return <CLBorradorBadge>Borrador</CLBorradorBadge>;
        if (row.resultado) return <CLResultBadge $res={row.resultado}>{row.resultado === 'operativo' ? 'Operativo' : 'Condicional'}</CLResultBadge>;
        return null;
      },
    },
    {
      key: 'puntos_malos',
      label: 'Fallas',
      width: '70px',
      render: (val) => val > 0 ? <CLFallas><i className="fas fa-exclamation-triangle" />{val}</CLFallas> : null,
    },
    {
      key: 'otc_numero',
      label: 'OTC',
      width: '120px',
      render: (val, row) => val ? (
        <CLOtcBadge
          title={`Ver OTC ${val}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/reparaciones/otc/${row.otc_generada}`); }}
        >{val}</CLOtcBadge>
      ) : null,
    },
    {
      key: '_chevron',
      label: '',
      width: '36px',
      render: () => <CLChevron className="bi bi-chevron-right" />,
    },
  ];

  const emptyStateNode = plantillaExiste === null ? (
    <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.4 }}>
      <i className="fas fa-spinner fa-spin" />
    </div>
  ) : plantillaExiste ? (
    <CLEmptyState>
      <i className="bi bi-clipboard" />
      <p>Todavía no hay check lists registrados para este equipo.</p>
      <small>Usá el botón <strong>Nuevo</strong> para registrar el primer control.</small>
    </CLEmptyState>
  ) : (
    <CLNoPlantillaNudge>
      <div className="nudge-icon"><i className="bi bi-clipboard-x" /></div>
      <p className="nudge-title">Sin plantilla configurada</p>
      <p className="nudge-sub">Esta familia no tiene puntos de control definidos. Configurá la plantilla para poder registrar el estado del equipo.</p>
      <CLConfigBtn onClick={() => navigate('/configuracion/checklist')}>
        <i className="bi bi-gear" /> Configurar plantilla <i className="bi bi-arrow-right" />
      </CLConfigBtn>
    </CLNoPlantillaNudge>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        <ListaUnificada
          titulo="Check Lists"
          icono="bi bi-clipboard-check"
          sinMarco={true}
          tituloGrande={true}
          tableVisualVariant="rows"
          endpoint={`check-lists/?equipo=${equipoId}`}
          columns={columnsCheckList}
          searchPlaceholder="Buscar check lists..."
          allowAdd={true}
          onAdd={() => setModalNuevo(true)}
          allowEdit={false}
          allowDelete={hasPermission('checklist', 'delete')}
          onDelete={handleEliminarCheckList}
          allowView={false}
          onRowClick={(item) => navigate(`/equipos/${equipoId}/checklist/${item.id}`)}
          additionalActions={plantillaId ? [{
            icon: 'fas fa-print',
            label: 'Imprimir',
            variant: 'secondary',
            onClick: () => imprimirCheckList({ plantillaId, equipoId, theme }),
          }] : []}
          emptyState={emptyStateNode}
          resizableColumns={true}
          itemsPerPageOptions={[10, 25, 50]}
          defaultItemsPerPage={10}
        />
      </div>

      {modalNuevo && (
        <CLModalOverlay onClick={() => !iniciando && setModalNuevo(false)}>
          <CLModalCard onClick={(e) => e.stopPropagation()}>
            <CLModalHeader>
              <CLModalTitle>
                <i className="bi bi-clipboard-plus" />
                Nuevo check list
              </CLModalTitle>
              <CLModalClose onClick={() => setModalNuevo(false)} disabled={iniciando}>
                <i className="bi bi-x" />
              </CLModalClose>
            </CLModalHeader>
            <CLModalBody>
              <CLTipoGrid>
                {[
                  { key: 'ingreso', icon: '⬇️', label: 'Ingreso', desc: 'El equipo entra a la obra o planta' },
                  { key: 'egreso',  icon: '⬆️', label: 'Egreso',  desc: 'El equipo sale hacia una obra o destino' },
                ].map((op) => (
                  <CLTipoCard
                    key={op.key}
                    $active={tipoNuevo === op.key}
                    onClick={() => setTipoNuevo(op.key)}
                  >
                    <CLTipoIcon>{op.icon}</CLTipoIcon>
                    <CLTipoLabel $active={tipoNuevo === op.key}>{op.label}</CLTipoLabel>
                    <CLTipoDesc>{op.desc}</CLTipoDesc>
                  </CLTipoCard>
                ))}
              </CLTipoGrid>
              <CLModalFooter>
                <CLModalCancelBtn onClick={() => setModalNuevo(false)} disabled={iniciando}>
                  Cancelar
                </CLModalCancelBtn>
                <CLModalPrimaryBtn onClick={handleIniciarNuevo} disabled={iniciando}>
                  {iniciando
                    ? <><i className="fas fa-spinner fa-spin" /> Iniciando...</>
                    : <><i className="bi bi-play-fill" /> Iniciar check list</>
                  }
                </CLModalPrimaryBtn>
              </CLModalFooter>
            </CLModalBody>
          </CLModalCard>
        </CLModalOverlay>
      )}
    </div>
  );
};

export default DetalleEquipo;